import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getCachedAccessibleStores } from '@/lib/runtimeMetaCache';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import {
 Select,
 SelectContent,
 SelectItem,
 SelectTrigger,
 SelectValue,
} from '@/components/ui/select';
import {
 Table,
 TableBody,
 TableCell,
 TableHead,
 TableHeader,
 TableRow,
} from '@/components/ui/table';
import {
 Dialog,
 DialogContent,
 DialogHeader,
 DialogTitle,
 DialogDescription,
 DialogFooter,
} from '@/components/ui/dialog';
import {
 Collapsible,
 CollapsibleContent,
 CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
 DropdownMenu,
 DropdownMenuContent,
 DropdownMenuItem,
 DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
 AlertDialog,
 AlertDialogAction,
 AlertDialogCancel,
 AlertDialogContent,
 AlertDialogDescription,
 AlertDialogFooter,
 AlertDialogHeader,
 AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
 Popover,
 PopoverContent,
 PopoverTrigger,
} from '@/components/ui/popover';
import {
 Command,
 CommandEmpty,
 CommandGroup,
 CommandInput,
 CommandItem,
 CommandList,
} from '@/components/ui/command';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@/components/ui/accordion';
import { toast } from 'sonner';
import { getApiBaseUrl } from '@/lib/apiBaseUrl';
import {
 getOrderFull,
 updateOrder,
previewLineItemFabricChange,
applyLineItemFabricChange,
bulkCreateTestGarments,
 updateOrderStatus,
 getProducts,
 getProductWithCustomizations,
 createPayment,
 updatePayment,
 deletePayment,
 createOrderInteraction,
 updateOrderInteraction,
 deleteOrderInteraction,
 createAlteration,
 updateAlteration,
 updateAlterationStatus,
 deleteAlteration,
 createTask,
 updateTask,
 updateTaskStatus,
 deleteTask,
 createFabricOrder,
 updateFabricOrder,
 deleteFabricOrder,
 updateFabricOrderStatus,
 inlineUpdateFabricOrder,
 getSuppliers,
 createGarment,
 updateGarment,
 updateGarmentStatus,
 deleteGarment,
 getAssignableUsers,
 sendOrderInvoiceEmail,
syncOrderToDailyStatus,
} from '@/lib/api';
import CopyFromOrderDialog from '@/components/orders/CopyFromOrderDialog';
import { buildCopiedLineItems } from '@/lib/copyLineItems';
import {
 ArrowLeft, Save, FileText, Package, CreditCard, Shirt,
 MessageSquare, Scissors, CheckSquare, Plus, Trash2, Edit2,
 Copy, Layers, Minus, X, ChevronDown, ChevronRight, Download, Mail,
 User, Calendar, DollarSign, Calculator, AlertCircle,
 MoreHorizontal, RefreshCw, Eye, EyeOff, Loader2, Send, History,
 Image as ImageIcon, ClipboardCopy, Search, Check, CheckCircle2, Link2
} from 'lucide-react';
import { format } from 'date-fns';
import { formatCurrencyWords } from '@/lib/numberToWords';
import { useAuth } from '@/context/AuthContext';
import { formatDateTimeWord } from '@/lib/dateFormat';
import DateField from '@/components/common/DateField';
import { getFabricReferencePanel } from '@/lib/fabricReference';
import GarmentImagesModal from '@/components/garments/GarmentImagesModal';
import {
  LineItemRow,
  GarmentSubForm,
  FabricOrderSubForm,
  OrderPaymentsTab,
  OrderTasksTab,
  OrderInteractionsTab,
  OrderAlterationsTab,
  OrderEmailsTab,
  OrderHistoryTab,
  OrderHeader,
  CustomerInfoCard,
  FinancialSummaryCard,
  findMatchingLineItem
} from '@/components/orders';
import AddGarmentDialog from '@/components/orders/AddGarmentDialog';
import OrderItemsViewToolbar from '@/components/orders/OrderItemsViewToolbar';
import { CustomizationFlowModal } from '@/components/customizations/CustomizationFlowModal';
import {
 isTestLineItem,
 isTestGarment,
 getLineItemTypeKey,
 getGarmentRecordTypeKey,
 filterByGarmentType,
} from '@/lib/orderItemFilters';

// ============== CONSTANTS ==============

const garmentStatusOptions = [
 { value: 'pending', label: 'Pending', color: 'bg-slate-600/80 text-white' },
 { value: 'cutting', label: 'Cutting', color: 'bg-blue-600/80 text-white' },
 { value: 'sewing', label: 'Sewing', color: 'bg-purple-600/80 text-white' },
 { value: 'finishing', label: 'Finishing', color: 'bg-cyan-600/80 text-white' },
 { value: 'quality_check', label: 'Quality Check', color: 'bg-indigo-600/80 text-white' },
 { value: 'completed', label: 'Completed', color: 'bg-emerald-600/80 text-white' },
];

// Note: stores will be fetched from API based on user's accessible stores
const paymentMethods = ['Cash', 'Credit Card', 'Debit Card', 'Bank Transfer', 'Check', 'PayPal', 'Other'];
const interactionTypes = ['Fitting', 'Collection', 'Consultation', 'Phone Call', 'Email', 'WhatsApp', 'Other'];
const fabricTypes = [
  { value: 'main_fabric', label: 'Main Fabric' },
  { value: 'lining', label: 'Lining' },
  { value: 'contrast', label: 'Contrast' },
  { value: 'interlining', label: 'Interlining' },
  { value: 'button', label: 'Button' },
  { value: 'thread', label: 'Thread' },
];
const priorityOptions = ['low', 'medium', 'high', 'urgent'];
const taskStatuses = ['pending', 'in_progress', 'completed', 'cancelled'];
const alterationStatuses = ['pending', 'in_progress', 'completed'];
const AUXILIARY_ORDER_TABS = ['payments', 'tasks', 'interactions', 'alterations'];
const TAB_TRIGGER_CLASS = "h-8 flex-1 min-w-0 px-1.5 py-1 text-[11px] leading-tight data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=active]:shadow-md rounded-md transition-all";

const getRecordSortTimestamp = (record, fallbackFields = []) => {
  const candidateFields = ['created_at', 'updated_at', ...fallbackFields];
  for (const fieldName of candidateFields) {
    const rawValue = record?.[fieldName];
    if (!rawValue) continue;
    const timestamp = new Date(rawValue).getTime();
    if (!Number.isNaN(timestamp) && timestamp > 0) {
      return timestamp;
    }
  }
  return 0;
};

const sortRecordsNewestFirst = (records = [], fallbackFields = [], idField = null) => (
  [...records].sort((left, right) => {
    const timestampDiff = getRecordSortTimestamp(right, fallbackFields) - getRecordSortTimestamp(left, fallbackFields);
    if (timestampDiff !== 0) {
      return timestampDiff;
    }
    if (idField) {
      return String(right?.[idField] || '').localeCompare(String(left?.[idField] || ''));
    }
    return 0;
  })
);

// ============== HELPER FUNCTIONS ==============
const formatCurrency = (amount) => {
 return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
};

const formatDateTime = (dateString) => {
 return formatDateTimeWord(dateString);
};
/**
 * Shared helper function to create line items with snapshot
 * Works for both regular garments and test garments
 */
const createLineItemWithSnapshot = (config) => {
  const {
    lineItemId,
    productId,
    productName,
    garmentType,
    quantity = 1,
    basePrice = 0,
    lineItemPrice = 0,
    customizationPriceImpact = 0,
    customizations = [],
    productCustomizations,
    productionFactoryId = null,
    dispatchFactoryId = null,
    isTest = false
  } = config;

  const lineItem = {
    line_item_id: lineItemId,
    product_id: productId,
    product_name: productName,
    quantity,
    production_factory_id: productionFactoryId || null,
    dispatch_factory_id: dispatchFactoryId || productionFactoryId || null,
    customizations,
    snapshot_customization_schema: productCustomizations, // Always save schema snapshot
    _needs_garment: true,
  };

  // Add garment_type only if provided (regular garments have it, test garments might not initially)
  if (garmentType) {
    lineItem.garment_type = garmentType;
  }

  // For test garments: zero prices and special flag
  if (isTest) {
    lineItem.base_price = 0;
    lineItem.line_item_price = 0;
    lineItem.customization_price_impact = 0;
    lineItem._is_test = true;
  } else {
    // For regular garments: use actual prices
    lineItem.base_price = basePrice;
    lineItem.line_item_price = lineItemPrice;
    lineItem.customization_price_impact = customizationPriceImpact;
  }

  return lineItem;
};

// Generate line item refs with order number prefix
const generateLineItemRefs = (lineItems, products, orderNumber) => {
 // IMPORTANT: Do NOT regenerate line_item_ref if it already exists!
 // Line item refs are permanent identifiers, not position-based counters
 const prefixCounts = {};
 const seenRefs = new Set();
 
 // First pass: count all existing UNIQUE refs to avoid duplicates
 lineItems.forEach(item => {
 if (item.line_item_ref) {
 // Check if this ref is a duplicate
 if (seenRefs.has(item.line_item_ref)) {
 // This is a duplicate ref - we'll need to regenerate it
 return;
 }
 seenRefs.add(item.line_item_ref);
 
 // Extract the prefix and number from existing ref (e.g., "PER-xxx-J2" -> prefix="J", count=2)
 const parts = item.line_item_ref.split('-');
 const lastPart = parts[parts.length - 1]; // e.g., "J2"
 const match = lastPart.match(/^([A-Z]+)(\d+)$/); // Match prefix letters and number
 if (match) {
 const prefix = match[1]; // e.g., "J"
 const num = parseInt(match[2], 10); // e.g., 2
 prefixCounts[prefix] = Math.max(prefixCounts[prefix] || 0, num);
 }
 }
 });
 
 // Reset seenRefs for second pass
 seenRefs.clear();
 
 // Second pass: generate refs for items without them OR with duplicate refs
 return lineItems.map(item => {
 // Check if line_item_ref exists and is unique
 if (item.line_item_ref && !seenRefs.has(item.line_item_ref)) {
 seenRefs.add(item.line_item_ref);
 return item;
 }
 
 // Generate new ref for items without one OR with duplicate refs
 const product = products.find(p => p.product_id === item.product_id);
 const prefix = product?.product_prefix || item.product_name?.charAt(0)?.toUpperCase() || 'P';
 prefixCounts[prefix] = (prefixCounts[prefix] || 0) + 1;
 
 const newRef = `${orderNumber}-${prefix}${prefixCounts[prefix]}`;
 seenRefs.add(newRef);
 
 return {
 ...item,
 line_item_ref: newRef
 };
 });
};


// ============== MAIN COMPONENT ==============
const EditOrderPage = () => {
 const { orderId } = useParams();
 const navigate = useNavigate();
 const { user } = useAuth();
 
 // Loading & State
 const [loading, setLoading] = useState(true);
 const [saving, setSaving] = useState(false);
 const [syncingDailyStatus, setSyncingDailyStatus] = useState(false);
 const [orderData, setOrderData] = useState(null);
 const [products, setProducts] = useState([]);
 const [suppliers, setSuppliers] = useState([]);
 const [stores, setStores] = useState([]);
 const [factories, setFactories] = useState([]);
 const [users, setUsers] = useState([]);
 const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
 const [saveStatus, setSaveStatus] = useState('idle'); // 'idle', 'saved', 'saving', 'error'
 const [autoSaveTimeout, setAutoSaveTimeout] = useState(null);
 const [activeTab, setActiveTab] = useState('line_items');
 const [lineItemViewMode, setLineItemViewMode] = useState('client');
 const [lineItemTypeFilter, setLineItemTypeFilter] = useState('all');
 const [garmentViewMode, setGarmentViewMode] = useState('client');
 const [garmentTypeFilter, setGarmentTypeFilter] = useState('all');
const [detailsHeavyReady, setDetailsHeavyReady] = useState(false);
const [detailsHeavyInitialized, setDetailsHeavyInitialized] = useState(false);
const [mountDeferredDialogs, setMountDeferredDialogs] = useState(false);
const [datePropagationModalOpen, setDatePropagationModalOpen] = useState(false);
const [datePropagationConfig, setDatePropagationConfig] = useState(null);
const [datePropagationTargets, setDatePropagationTargets] = useState({
garmentIds: [],
});
const dependencyLoadedRef = useRef({
products: false,
suppliers: false,
users: false,
stores: false,
factories: false,
});
const dependencyLoadingRef = useRef({
products: false,
suppliers: false,
users: false,
stores: false,
factories: false,
});

 // Form State
 const [formData, setFormData] = useState({
 customer_id: '',
 order_date: '',
 client_final_date: '',
 internal_delivery_date: '',
 test_fit_date: '',
 store: '__none__',
 notes: '',
 gst_percentage: 0,
 discount_type: '__none__',
 discount_value: 0,
 status: 'pending_payment',
 });

 // Line Items State
 const [lineItems, setLineItems] = useState([]);
 const [selectedLineItems, setSelectedLineItems] = useState([]);
const [lineItemFactorySelection, setLineItemFactorySelection] = useState('__none__');

 // Dialog States
 const [showBackConfirm, setShowBackConfirm] = useState(false);
 const [addLineItemOpen, setAddLineItemOpen] = useState(false);
 const [copyDialogOpen, setCopyDialogOpen] = useState(false);
 const [reassigningCustomer, setReassigningCustomer] = useState(false);
 const [addLineItemCustomizationOpen, setAddLineItemCustomizationOpen] = useState(false);
 const [editLineItemOpen, setEditLineItemOpen] = useState(false);
 const [editingLineItem, setEditingLineItem] = useState(null);
 const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
 const [editingPayment, setEditingPayment] = useState(null);
 const [interactionDialogOpen, setInteractionDialogOpen] = useState(false);
 const [editingInteraction, setEditingInteraction] = useState(null);
 const [alterationDialogOpen, setAlterationDialogOpen] = useState(false);
 const [editingAlteration, setEditingAlteration] = useState(null);
 const [taskDialogOpen, setTaskDialogOpen] = useState(false);
 const [editingTask, setEditingTask] = useState(null);
 const [fabricOrderDialogOpen, setFabricOrderDialogOpen] = useState(false);
 const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
 const [itemToDelete, setItemToDelete] = useState(null);
 const [garmentImagesOpen, setGarmentImagesOpen] = useState(false);
 const [selectedGarment, setSelectedGarment] = useState(null);
 const [duplicateLineItemOpen, setDuplicateLineItemOpen] = useState(false);
 const [duplicatingLineItem, setDuplicatingLineItem] = useState(null);
 const [deleteGarmentDialogOpen, setDeleteGarmentDialogOpen] = useState(false);
 const [garmentToDelete, setGarmentToDelete] = useState(null);
 const [deleteGarmentOption, setDeleteGarmentOption] = useState('garment_only');
 const [addGarmentDialogOpen, setAddGarmentDialogOpen] = useState(false);
 const [remakeGarmentData, setRemakeGarmentData] = useState(null);

 // Test Garment Wizard State
 const [testGarmentOpen, setTestGarmentOpen] = useState(false);
 const [testGarmentStep, setTestGarmentStep] = useState(1); // 1: Select Type, 2: Choose Mode, 3: Select Categories, 4: Configure Options
 const [selectedTestProduct, setSelectedTestProduct] = useState(null);
 const [testGarmentMode, setTestGarmentMode] = useState('blank'); // 'blank' | 'mimic' | 'quick_multi_mimic'
 const [mimicSourceGarment, setMimicSourceGarment] = useState(null);
const [testGarmentFactorySelection, setTestGarmentFactorySelection] = useState('__none__');
 const [mimicCategories, setMimicCategories] = useState([]); // Categories to copy
 const [sourceCustomizations, setSourceCustomizations] = useState(null);
 const [testGarmentCustomValues, setTestGarmentCustomValues] = useState({}); // Option values for blank/mimic mode
 const [quickMultiMimicSelection, setQuickMultiMimicSelection] = useState({});
 const [quickMultiMimicSelectedCategories, setQuickMultiMimicSelectedCategories] = useState([]);
 const [quickMultiMimicSubmitting, setQuickMultiMimicSubmitting] = useState(false);
 const [testPlannerCounts, setTestPlannerCounts] = useState({});
 const [testPlannerQueue, setTestPlannerQueue] = useState([]);
 const [testPlannerCurrentIndex, setTestPlannerCurrentIndex] = useState(0);
 const [testPlannerSubmitting, setTestPlannerSubmitting] = useState(false);
 const [testPlannerSubStep, setTestPlannerSubStep] = useState('source'); // source | categories | options
 const testPlannerSourceAutoPickRef = useRef({});

// Line-item fabric sync confirmation state (when fabric IDs change)
const [fabricSyncDialogOpen, setFabricSyncDialogOpen] = useState(false);
const [fabricSyncPreview, setFabricSyncPreview] = useState(null);
const [fabricSyncSelections, setFabricSyncSelections] = useState({});
const [pendingLineItemSaveContext, setPendingLineItemSaveContext] = useState(null);

 // Product customizations for add/edit line item
 const [selectedProduct, setSelectedProduct] = useState(null);
 const [productCustomizations, setProductCustomizations] = useState(null);
 const [customizationValues, setCustomizationValues] = useState({});
 const [lineItemQuantity, setLineItemQuantity] = useState(1);
 const [basePrice, setBasePrice] = useState(null);

 // Field deletion states
 const [fieldToDelete, setFieldToDelete] = useState(null);
 const [deleteFieldDialogOpen, setDeleteFieldDialogOpen] = useState(false);

 // Custom field addition states
 const [addCustomFieldDialogOpen, setAddCustomFieldDialogOpen] = useState(false);
 const [customFieldCategoryId, setCustomFieldCategoryId] = useState(null);
 const [customFieldForm, setCustomFieldForm] = useState({ field_name: '', field_value: '' });

const anyDialogOpen =
showBackConfirm ||
deleteConfirmOpen ||
fabricSyncDialogOpen ||
datePropagationModalOpen ||
addLineItemOpen ||
copyDialogOpen ||
addLineItemCustomizationOpen ||
editLineItemOpen ||
duplicateLineItemOpen ||
paymentDialogOpen ||
interactionDialogOpen ||
alterationDialogOpen ||
taskDialogOpen ||
fabricOrderDialogOpen ||
testGarmentOpen ||
deleteGarmentDialogOpen ||
deleteFieldDialogOpen ||
addCustomFieldDialogOpen ||
addGarmentDialogOpen ||
garmentImagesOpen;

const shouldMountDialogs = mountDeferredDialogs || anyDialogOpen;

 // Form states for dialogs
 const [paymentForm, setPaymentForm] = useState({ amount: '', payment_method: '__none__', payment_date: '', notes: '' });
 const [interactionForm, setInteractionForm] = useState({ interaction_type: '__none__', interaction_date: '', notes: '', conducted_by: '' });
 const [alterationForm, setAlterationForm] = useState({ garment_id: '__none__', description: '', cost: '', tailor_name: '', alteration_date: '', status: 'pending' });
 const [taskForm, setTaskForm] = useState({ title: '', description: '', assigned_to: [], due_date: '', priority: 'medium', status: 'pending' });
 const [fabricOrderForm, setFabricOrderForm] = useState({ garment_id: '__none__', supplier_id: '__none__', fabric_sku: '', fabric_type: 'main_fabric', quantity: '', notes: '' });

const normalizeDateInput = (value) => {
if (!value) return '';
return String(value).split('T')[0];
};

const selectedDatePropagationGarmentIds = useMemo(() => new Set(datePropagationTargets.garmentIds || []), [datePropagationTargets.garmentIds]);

const testGarments = useMemo(() => {
  const lineItems = orderData?.line_items || [];
  return (orderData?.garments || []).filter((garment) => {
    const linkedLineItem = lineItems.find((item) => item.line_item_id === garment.line_item_id);
    return isTestGarment(garment, linkedLineItem);
  });
}, [orderData?.garments, orderData?.line_items]);

const relevantGarmentsForDateModal = useMemo(() => {
if (!datePropagationConfig) return [];
if (datePropagationConfig.type === 'test_fit_date') {
 return testGarments;
}
return orderData?.garments || [];
}, [datePropagationConfig, orderData?.garments, testGarments]);

const handleDateFieldChangeRequest = (field, value) => {
const nextDate = normalizeDateInput(value);
const currentDate = normalizeDateInput(formData[field]);

if (nextDate === currentDate) {
 setFormData((prev) => ({ ...prev, [field]: nextDate }));
 return;
}

if (field === 'order_date') {
 setFormData((prev) => ({ ...prev, [field]: nextDate }));
 setHasUnsavedChanges(true);
 return;
}

const applicableGarments = field === 'test_fit_date'
  ? testGarments
  : (orderData?.garments || []);

setFormData((prev) => ({ ...prev, [field]: nextDate }));
setHasUnsavedChanges(true);

setDatePropagationTargets({
 garmentIds: applicableGarments.map((garment) => garment.garment_id),
});

setDatePropagationConfig({
 field,
 value: nextDate,
 type: field,
});
setDatePropagationModalOpen(true);
};

const toggleDatePropagationGarmentSelection = (garmentId, checked) => {
setDatePropagationTargets((prev) => ({
 ...prev,
 garmentIds: checked
  ? [...new Set([...(prev.garmentIds || []), garmentId])]
  : (prev.garmentIds || []).filter((id) => id !== garmentId),
}));
};

const toggleDatePropagationSelectAllGarments = (checked) => {
setDatePropagationTargets((prev) => ({
 ...prev,
 garmentIds: checked ? (relevantGarmentsForDateModal || []).map((garment) => garment.garment_id) : [],
}));
};

const applyDatePropagationTargets = async () => {
if (!datePropagationConfig) return;

const { field, value } = datePropagationConfig;
const nextValue = value || null;
const selectedGarmentIds = new Set(datePropagationTargets.garmentIds || []);

if (selectedGarmentIds.size > 0) {
 const mergedGarments = (orderData?.garments || []).map((garment) => {
  if (!selectedGarmentIds.has(garment.garment_id)) return garment;
  if (field === 'internal_delivery_date') {
   return { ...garment, internal_delivery_date: nextValue };
  }
  if (field === 'client_final_date') {
   return { ...garment, client_final_date: nextValue };
  }
  if (field === 'test_fit_date') {
   return { ...garment, test_fit_date: nextValue };
  }
  return garment;
 });

 setOrderData((prev) => prev ? ({ ...prev, garments: mergedGarments }) : prev);
}

setHasUnsavedChanges(true);
setDatePropagationModalOpen(false);
setDatePropagationConfig(null);
};

const skipDatePropagationTargets = () => {
setDatePropagationModalOpen(false);
setDatePropagationConfig(null);
setHasUnsavedChanges(true);
};

 // ============== DATA FETCHING ==============
 useEffect(() => {
fetchEditBootstrap();
 }, [orderId]);

useEffect(() => {
setDetailsHeavyReady(false);
setDetailsHeavyInitialized(false);
}, [orderId]);

useEffect(() => {
const timeout = setTimeout(() => {
setMountDeferredDialogs(true);
}, 400);

return () => clearTimeout(timeout);
}, []);

useEffect(() => {
if (!['line_items', 'garments', 'fabric_orders'].includes(activeTab)) return;
if (!orderData?.order_id) return;
if (detailsHeavyInitialized) return;

const timeout = setTimeout(() => {
setDetailsHeavyReady(true);
setDetailsHeavyInitialized(true);
}, 0);

return () => clearTimeout(timeout);
}, [activeTab, orderData?.order_id, detailsHeavyInitialized]);

useEffect(() => {
 if (activeTab === 'line_items') {
  fetchProducts();
  fetchFactories();
 }
 if (activeTab === 'garments') {
  fetchFactories();
 }
 if (activeTab === 'fabric_orders') {
  fetchFactories();
  fetchSuppliers();
 }
 if (activeTab === 'tasks') {
  fetchUsers();
 }
}, [activeTab]);

useEffect(() => {
 if (!AUXILIARY_ORDER_TABS.includes(activeTab)) return;
 if (!orderData?.order_id) return;
 if (orderData?.auxiliary_loaded) return;
 fetchOrderData({ silent: true });
}, [activeTab, orderData?.order_id, orderData?.auxiliary_loaded]);

 useEffect(() => {
 // Track changes - include GST and discount changes
 if (orderData) {
 const hasChanges = JSON.stringify(lineItems) !== JSON.stringify(orderData.line_items) ||
 formData.customer_id !== (orderData.customer_id || '') ||
 formData.order_date !== (orderData.order_date?.split('T')[0] || '') ||
 formData.client_final_date !== (orderData.client_final_date?.split('T')[0] || '') ||
 formData.internal_delivery_date !== (orderData.internal_delivery_date?.split('T')[0] || '') ||
 formData.test_fit_date !== (orderData.test_fit_date?.split('T')[0] || '') ||
 formData.store !== (orderData.store || '__none__') ||
 formData.notes !== (orderData.notes || '') ||
 formData.status !== orderData.status ||
 parseFloat(formData.gst_percentage) !== (orderData.gst_percentage || 0) ||
 formData.discount_type !== (orderData.discount_type || '__none__') ||
 parseFloat(formData.discount_value) !== (orderData.discount_value || 0);
 setHasUnsavedChanges(hasChanges);
 }
 }, [formData, lineItems, orderData]);

 // Helper function to check if an option should be visible based on conditional logic
 const isOptionVisible = (option, customizationValues) => {
   // If no conditional logic, always show
   if (!option.conditional_on_option_id) return true;
   
   const parentValue = customizationValues[option.conditional_on_option_id];
   const operator = option.conditional_operator || 'equals';
   const expectedValues = option.conditional_on_values || [];
   
   // Evaluate based on operator
   switch (operator) {
     case 'equals':
       if (!parentValue) return false;
       if (expectedValues.length > 0) {
         return expectedValues.includes(parentValue);
       }
       return true;
     
     case 'not_equals':
       if (!parentValue) return expectedValues.length > 0;
       if (expectedValues.length > 0) {
         return !expectedValues.includes(parentValue);
       }
       return false;
     
     case 'is_empty':
       return !parentValue || parentValue.trim() === '';
     
     case 'is_not_empty':
       return parentValue && parentValue.trim() !== '';
     
     case 'contains':
       if (!parentValue) return false;
       if (expectedValues.length > 0) {
         return expectedValues.some(val => parentValue.toLowerCase().includes(val.toLowerCase()));
       }
       return false;
     
     case 'not_contains':
       // Show if parent value does NOT contain any of the expected values
       // If parent is empty, hide the child (can't verify it doesn't contain something)
       if (!parentValue || parentValue.trim() === '') return false;
       if (expectedValues.length > 0) {
         return !expectedValues.some(val => parentValue.toLowerCase().includes(val.toLowerCase()));
       }
       return true;
     
     case 'greater_than':
       if (!parentValue || expectedValues.length === 0) return false;
       const numValue = parseFloat(parentValue);
       const threshold = parseFloat(expectedValues[0]);
       return !isNaN(numValue) && !isNaN(threshold) && numValue > threshold;
     
     case 'less_than':
       if (!parentValue || expectedValues.length === 0) return false;
       const numVal = parseFloat(parentValue);
       const limit = parseFloat(expectedValues[0]);
       return !isNaN(numVal) && !isNaN(limit) && numVal < limit;
     
     default:
       return true;
   }
 };

 const isMainOrLiningFabricOption = (option, fallbackOptionName = '') => {
   const selectorType = String(option?.fabric_selector_type || '').toLowerCase();
   const isSelector = Boolean(option?.is_fabric_selector) || Boolean(selectorType);

   if (isSelector) {
     return ['main', 'main_fabric', 'lining', 'lining_fabric'].includes(selectorType);
   }

   // Fallback for legacy/missing metadata
   const optionName = String(option?.name || fallbackOptionName || '').toLowerCase();
   const normalized = optionName.replace(/\s+/g, ' ').trim();
   return (
     normalized.includes('main/base fabric id') ||
     normalized.includes('main fabric id') ||
     normalized.includes('lining fabric id')
   );
 };

const fetchEditBootstrap = async () => {
 setLoading(true);
 try {
 const response = await getOrderFull(orderId, { include_auxiliary: false });
 const orderPayload = response?.data || {};

 setOrderData(orderPayload);
 setFormData({
 customer_id: orderPayload.customer_id || '',
 order_date: orderPayload.order_date?.split('T')[0] || '',
 client_final_date: orderPayload.client_final_date?.split('T')[0] || '',
 internal_delivery_date: orderPayload.internal_delivery_date?.split('T')[0] || '',
 test_fit_date: orderPayload.test_fit_date?.split('T')[0] || '',
 store: orderPayload.store || '__none__',
 notes: orderPayload.notes || '',
 gst_percentage: orderPayload.gst_percentage || 0,
 discount_type: orderPayload.discount_type || '__none__',
 discount_value: orderPayload.discount_value || 0,
 status: orderPayload.status,
 });
 setLineItems(orderPayload.line_items || []);

 // Non-blocking dependency warm-up (stores needed only for optional header store edit)
 setTimeout(() => {
  fetchStores();
 }, 0);
 } catch (error) {
 toast.error('Failed to load order');
 navigate('/orders');
 } finally {
 setLoading(false);
 }
};

const fetchOrderData = async ({ silent = false } = {}) => {
if (!silent) {
setLoading(true);
}
 try {
 const includeAuxiliary = AUXILIARY_ORDER_TABS.includes(activeTab);
 const response = await getOrderFull(orderId, { include_auxiliary: includeAuxiliary });
 setOrderData(response.data);
 setFormData({
 customer_id: response.data.customer_id || '',
 order_date: response.data.order_date?.split('T')[0] || '',
 client_final_date: response.data.client_final_date?.split('T')[0] || '',
 internal_delivery_date: response.data.internal_delivery_date?.split('T')[0] || '',
 test_fit_date: response.data.test_fit_date?.split('T')[0] || '',
 store: response.data.store || '__none__',
 notes: response.data.notes || '',
 gst_percentage: response.data.gst_percentage || 0,
 discount_type: response.data.discount_type || '__none__',
 discount_value: response.data.discount_value || 0,
 status: response.data.status,
 });
 setLineItems(response.data.line_items || []);
 
 // Mark main data as loaded but don't load tab data yet
 // Payments, interactions, alterations, tasks will be lazy loaded
 } catch (error) {
 toast.error('Failed to load order');
 navigate('/orders');
} finally {
if (!silent) {
setLoading(false);
}
}
 };

const fetchProducts = async () => {
 try {
  if (dependencyLoadedRef.current.products) return products;
  if (dependencyLoadingRef.current.products) return products;
  dependencyLoadingRef.current.products = true;
  const response = await getProducts();
  const productRows = Array.isArray(response.data) ? response.data : [];
  setProducts(productRows);
  dependencyLoadedRef.current.products = true;
  return productRows;
 } catch (error) {
  console.error('Failed to fetch products:', error);
  return products;
 } finally {
  dependencyLoadingRef.current.products = false;
 }
};

const fetchSuppliers = async () => {
 try {
  if (dependencyLoadedRef.current.suppliers || dependencyLoadingRef.current.suppliers) return;
  dependencyLoadingRef.current.suppliers = true;
  const response = await getSuppliers();
  setSuppliers(Array.isArray(response.data) ? response.data : []);
  dependencyLoadedRef.current.suppliers = true;
 } catch (error) {
  console.error('Failed to fetch suppliers:', error);
 } finally {
  dependencyLoadingRef.current.suppliers = false;
 }
};

const fetchUsers = async () => {
 try {
  if (dependencyLoadedRef.current.users || dependencyLoadingRef.current.users) return;
  dependencyLoadingRef.current.users = true;
  const response = await getAssignableUsers();
  setUsers(Array.isArray(response.data) ? response.data : []);
  dependencyLoadedRef.current.users = true;
 } catch (error) {
  console.error('Failed to fetch users:', error);
 } finally {
  dependencyLoadingRef.current.users = false;
 }
};

const fetchStores = async () => {
 try {
  if (dependencyLoadedRef.current.stores || dependencyLoadingRef.current.stores) return;
  dependencyLoadingRef.current.stores = true;
  const storesData = await getCachedAccessibleStores();
  setStores(Array.isArray(storesData) ? storesData : []);
  dependencyLoadedRef.current.stores = true;
 } catch (error) {
  console.error('Failed to fetch stores:', error);
 } finally {
  dependencyLoadingRef.current.stores = false;
 }
};

const fetchFactories = async () => {
 try {
  if (dependencyLoadedRef.current.factories || dependencyLoadingRef.current.factories) return;
  dependencyLoadingRef.current.factories = true;
  const token = localStorage.getItem('token');
  const API_URL = getApiBaseUrl();
  const response = await fetch(`${API_URL}/api/factories?active_only=true`, {
   headers: { Authorization: `Bearer ${token}` },
  });
  if (response.ok) {
   const factoriesData = await response.json();
   setFactories(Array.isArray(factoriesData) ? factoriesData : []);
   dependencyLoadedRef.current.factories = true;
  } else {
   setFactories([]);
  }
 } catch (error) {
  console.error('Failed to fetch factories:', error);
  setFactories([]);
 } finally {
  dependencyLoadingRef.current.factories = false;
 }
};

const clearFabricSyncFlow = () => {
 setFabricSyncDialogOpen(false);
 setFabricSyncPreview(null);
 setFabricSyncSelections({});
 setPendingLineItemSaveContext(null);
};

const persistEditedLineItemChanges = async ({
 updatedLineItems,
 lineItemId,
 oldCustomizations,
 newCustomizations,
 applyFabricSync = false,
}) => {
 setSaveStatus('saving');
 try {
 const itemsWithRefs = generateLineItemRefs(updatedLineItems, products, orderData.order_number);

 await updateOrder(orderId, {
 customer_id: formData.customer_id || orderData.customer_id,
order_date: formData.order_date || null,
status: formData.status,
 client_final_date: formData.client_final_date || null,
 internal_delivery_date: formData.internal_delivery_date || null,
 test_fit_date: formData.test_fit_date || null,
 store: formData.store === '__none__' ? null : formData.store,
 notes: formData.notes,
 gst_percentage: parseFloat(formData.gst_percentage) || 0,
 discount_type: formData.discount_type === '__none__' ? null : formData.discount_type,
 discount_value: parseFloat(formData.discount_value) || 0,
 line_items: itemsWithRefs,
 });

 if (applyFabricSync && lineItemId) {
   const garment_decisions = Object.entries(fabricSyncSelections).map(([garment_id, decision]) => ({
     garment_id,
     decision,
   }));

   const syncResponse = await applyLineItemFabricChange(orderId, lineItemId, {
     old_customizations: oldCustomizations || [],
     new_customizations: newCustomizations || [],
     garment_decisions,
   });

   const syncResult = syncResponse.data?.result || {};
   const warningCount = (syncResult.warnings || []).length;
   toast.success(
     `Line item saved. Fabric sync: ${syncResult.updated_count || 0} updated, ${syncResult.created_count || 0} created${warningCount ? `, ${warningCount} warning(s)` : ''}.`
   );
 }

 setLineItems(updatedLineItems);
 setSaveStatus('saved');
 setHasUnsavedChanges(false);
 await fetchOrderData();

 setTimeout(() => {
 setSaveStatus('idle');
 }, 2000);

 return true;
 } catch (error) {
 console.error('Failed to save line item update:', error);
 setSaveStatus('error');
 toast.error('Failed to save line item changes');
 return false;
 }
};

 // ============== COMPUTED VALUES ==============
 const lineItemsWithRefs = useMemo(() => {
 return generateLineItemRefs(lineItems, products, orderData?.order_number || '');
 }, [lineItems, products, orderData?.order_number]);

const sortedLineItemsWithRefs = useMemo(
  () => sortRecordsNewestFirst(lineItemsWithRefs, [], 'line_item_id'),
  [lineItemsWithRefs]
);

const clientLineItems = useMemo(
  () => sortedLineItemsWithRefs.filter((item) => !isTestLineItem(item)),
  [sortedLineItemsWithRefs]
);

const testLineItems = useMemo(
  () => sortedLineItemsWithRefs.filter((item) => isTestLineItem(item)),
  [sortedLineItemsWithRefs]
);

const lineItemsInCurrentView = lineItemViewMode === 'test' ? testLineItems : clientLineItems;

const lineItemTypeCounts = useMemo(() => {
  const counts = {};
  lineItemsInCurrentView.forEach((item) => {
    const typeKey = getLineItemTypeKey(item);
    counts[typeKey] = (counts[typeKey] || 0) + 1;
  });
  return counts;
}, [lineItemsInCurrentView]);

const visibleLineItems = useMemo(
  () => filterByGarmentType(lineItemsInCurrentView, lineItemTypeFilter, getLineItemTypeKey),
  [lineItemsInCurrentView, lineItemTypeFilter]
);

const sortedGarments = useMemo(
  () => sortRecordsNewestFirst(orderData?.garments || [], [], 'garment_id'),
  [orderData?.garments]
);

const primaryLinkedGarmentByLineItemId = useMemo(() => {
 const garmentsByLineItem = new Map();

 (orderData?.garments || []).forEach((garment) => {
  if (!garment?.line_item_id) return;
  const current = garmentsByLineItem.get(garment.line_item_id) || [];
  current.push(garment);
  garmentsByLineItem.set(garment.line_item_id, current);
 });

 const entries = Array.from(garmentsByLineItem.entries()).map(([lineItemId, garments]) => {
  const baseGarments = garments.filter((garment) => !/-Re\d+$/i.test(garment?.garment_no || ''));
  const candidates = baseGarments.length > 0 ? baseGarments : garments;
  const primaryGarment = [...candidates].sort((left, right) => {
   const timestampDiff = getRecordSortTimestamp(left) - getRecordSortTimestamp(right);
   if (timestampDiff !== 0) return timestampDiff;
   return String(left?.garment_no || '').localeCompare(String(right?.garment_no || ''));
  })[0] || null;

  return [lineItemId, primaryGarment];
 });

 return Object.fromEntries(entries);
}, [orderData?.garments]);

const lineItemById = useMemo(() => {
  const map = new Map();
  sortedLineItemsWithRefs.forEach((item) => {
    if (item?.line_item_id) map.set(item.line_item_id, item);
  });
  return map;
}, [sortedLineItemsWithRefs]);

const isGarmentInTestView = (garment) => (
  isTestGarment(garment, lineItemById.get(garment?.line_item_id))
);

const clientGarments = useMemo(
  () => sortedGarments.filter((garment) => !isGarmentInTestView(garment)),
  [sortedGarments, lineItemById]
);

const testGarmentRecords = useMemo(
  () => sortedGarments.filter((garment) => isGarmentInTestView(garment)),
  [sortedGarments, lineItemById]
);

const garmentsInCurrentView = garmentViewMode === 'test' ? testGarmentRecords : clientGarments;

const garmentTypeCounts = useMemo(() => {
  const counts = {};
  garmentsInCurrentView.forEach((garment) => {
    const linkedLineItem = sortedLineItemsWithRefs.find((item) => item.line_item_id === garment.line_item_id);
    const typeKey = getGarmentRecordTypeKey(garment, linkedLineItem);
    counts[typeKey] = (counts[typeKey] || 0) + 1;
  });
  return counts;
}, [garmentsInCurrentView, sortedLineItemsWithRefs]);

const visibleGarments = useMemo(
  () => filterByGarmentType(garmentsInCurrentView, garmentTypeFilter, (garment) => {
    const linkedLineItem = sortedLineItemsWithRefs.find((item) => item.line_item_id === garment.line_item_id);
    return getGarmentRecordTypeKey(garment, linkedLineItem);
  }),
  [garmentsInCurrentView, garmentTypeFilter, sortedLineItemsWithRefs]
);

const sortedFabricOrders = useMemo(
  () => sortRecordsNewestFirst(orderData?.fabric_orders || [], [], 'fabric_id'),
  [orderData?.fabric_orders]
);

const sortedPayments = useMemo(
  () => sortRecordsNewestFirst(orderData?.payments || [], ['payment_date'], 'payment_id'),
  [orderData?.payments]
);

const sortedTasks = useMemo(
  () => sortRecordsNewestFirst(orderData?.tasks || [], ['due_date'], 'task_id'),
  [orderData?.tasks]
);

const sortedInteractions = useMemo(
  () => sortRecordsNewestFirst(orderData?.interactions || [], ['interaction_date'], 'interaction_id'),
  [orderData?.interactions]
);

const sortedAlterations = useMemo(
  () => sortRecordsNewestFirst(orderData?.alterations || [], ['alteration_date'], 'alteration_id'),
  [orderData?.alterations]
);

const sortedOrderData = useMemo(() => {
  if (!orderData) return orderData;
  return {
    ...orderData,
    garments: sortedGarments,
    fabric_orders: sortedFabricOrders,
    payments: sortedPayments,
    tasks: sortedTasks,
    interactions: sortedInteractions,
    alterations: sortedAlterations,
  };
}, [orderData, sortedGarments, sortedFabricOrders, sortedPayments, sortedTasks, sortedInteractions, sortedAlterations]);

 const calculatedTotals = useMemo(() => {
 const subtotal = lineItems.reduce((sum, item) => sum + (item.line_item_price * item.quantity), 0);
 let discountAmount = 0;
 let discountPercentage = 0;
 if (formData.discount_type === 'percentage' && formData.discount_value > 0) {
 discountPercentage = parseFloat(formData.discount_value);
 discountAmount = subtotal * (discountPercentage / 100);
 } else if (formData.discount_type === 'fixed' && formData.discount_value > 0) {
 discountAmount = parseFloat(formData.discount_value);
 }
 const afterDiscount = subtotal - discountAmount;
 const gstPercentage = parseFloat(formData.gst_percentage) || 0;
 const gstAmount = afterDiscount * (gstPercentage / 100);
 const grandTotal = afterDiscount + gstAmount;
 return { 
 subtotal, 
 discount: discountAmount,
 discountPercentage,
 gst: gstAmount,
 gstPercentage,
 grandTotal,
 // Keep orderValue for backward compatibility
 orderValue: grandTotal
 };
 }, [lineItems, formData.discount_type, formData.discount_value, formData.gst_percentage]);

useEffect(() => {
 const visibleIds = new Set(visibleLineItems.map((item) => item.line_item_id));
 setSelectedLineItems((prev) => {
  const next = prev.filter((id) => visibleIds.has(id));
  if (next.length === prev.length) return prev;
  return next;
 });
}, [lineItemViewMode, lineItemTypeFilter, visibleLineItems]);

 // ============== HANDLERS ==============
const handleStatusSave = async () => {
  setSaving(true);
  try {
    const nextStatus = formData.status;
    const wasEnquiry = orderData?.status === 'enquiry';
    const isNowOrder = nextStatus && nextStatus !== 'enquiry';

    await updateOrderStatus(orderId, nextStatus);
    toast.success('Status updated successfully');

    // Full refresh so newly generated garments/fabric orders appear immediately
    await fetchOrderData();

    // If converting enquiry → order, keep the user informed (backend auto-generates)
    if (wasEnquiry && isNowOrder) {
      toast.success('Order created: garments & fabric orders generated');
    }
  } catch (error) {
    toast.error('Failed to update status');
  } finally {
    setSaving(false);
  }
};

const showGarmentDateSyncFeedback = (responseData) => {
 const summary = responseData?.date_sync_summary;
 if (!summary) return;

 const cddFilled = summary?.client_final_date?.auto_filled_count || 0;
 const iddFilled = summary?.internal_delivery_date?.auto_filled_count || 0;
 const cddMismatch = summary?.client_final_date?.mismatch_count || 0;
 const iddMismatch = summary?.internal_delivery_date?.mismatch_count || 0;

 if (cddFilled > 0 || iddFilled > 0) {
   toast.success(`Auto-filled garment dates: CDD ${cddFilled}, IDD ${iddFilled}`);
 }

 if (cddMismatch > 0 || iddMismatch > 0) {
   toast.warning(`Some garments already had different IDD/CDD (CDD ${cddMismatch}, IDD ${iddMismatch}). Please review garment-level dates.`);
 }
};

 const handleBack = () => {
 if (hasUnsavedChanges) {
 setShowBackConfirm(true);
 } else {
 navigate('/orders');
 }
 };

 const handleSave = async () => {
 setSaving(true);
 setSaveStatus('saving');
 try {
 const itemsWithRefs = generateLineItemRefs(lineItems, products, orderData.order_number);
 
 // Validate conversion from enquiry to committed order
 if (orderData.status === 'enquiry' && formData.status && 
     ['processing', 'pending_payment'].includes(formData.status)) {
   // Check if all line items have required customizations
   const hasIncompleteItems = lineItems.some(item => {
     const customizations = item.customizations || [];
     // Check if product has mandatory customizations that aren't filled
     return customizations.length === 0; // Simplified check - can be enhanced
   });
   
   if (hasIncompleteItems) {
     toast.error('Cannot convert to order: Please complete all mandatory customizations for line items');
     setSaving(false);
     return;
   }
   
   // Show confirmation dialog
   const confirmed = window.confirm(
     'Converting this enquiry to an order. All mandatory fields must be completed. Continue?'
   );
   
   if (!confirmed) {
     setSaving(false);
     return;
   }
 }
 const orderUpdateResponse = await updateOrder(orderId, {
 customer_id: formData.customer_id || orderData.customer_id,
order_date: formData.order_date || null,
 status: formData.status,
 client_final_date: formData.client_final_date || null,
 internal_delivery_date: formData.internal_delivery_date || null,
 test_fit_date: formData.test_fit_date || null,
 store: formData.store === '__none__' ? null : formData.store,
 notes: formData.notes,
 gst_percentage: parseFloat(formData.gst_percentage) || 0,
 discount_type: formData.discount_type === '__none__' ? null : formData.discount_type,
 discount_value: parseFloat(formData.discount_value) || 0,
 line_items: itemsWithRefs,
 });
 showGarmentDateSyncFeedback(orderUpdateResponse?.data);
 
 if (formData.status !== orderData.status) {
 await updateOrderStatus(orderId, formData.status);
 }
 
 setSaveStatus('saved');
 setHasUnsavedChanges(false);
 
 // Hide saved indicator after 2 seconds
 setTimeout(() => {
 setSaveStatus('idle');
 }, 2000);
 } catch (error) {
 console.error('Failed to save order:', error);
 setSaveStatus('error');
 } finally {
 setSaving(false);
 }
 };

 const handleReassignCustomer = async (customer) => {
 if (!customer?.customer_id) return;
 setReassigningCustomer(true);
 setSaving(true);
 try {
 setFormData((prev) => ({ ...prev, customer_id: customer.customer_id }));
 const itemsWithRefs = generateLineItemRefs(lineItems, products, orderData.order_number);
 await updateOrder(orderId, {
 customer_id: customer.customer_id,
 order_date: formData.order_date || null,
 status: formData.status,
 client_final_date: formData.client_final_date || null,
 internal_delivery_date: formData.internal_delivery_date || null,
 test_fit_date: formData.test_fit_date || null,
 store: formData.store === '__none__' ? null : formData.store,
 notes: formData.notes,
 gst_percentage: parseFloat(formData.gst_percentage) || 0,
 discount_type: formData.discount_type === '__none__' ? null : formData.discount_type,
 discount_value: parseFloat(formData.discount_value) || 0,
 line_items: itemsWithRefs,
 });
 toast.success(`Order assigned to ${customer.customer_name}`);
 setHasUnsavedChanges(false);
 await fetchOrderData();
 } catch (error) {
 console.error('Failed to reassign customer:', error);
 toast.error(error.response?.data?.detail || 'Failed to change client');
 throw error;
 } finally {
 setReassigningCustomer(false);
 setSaving(false);
 }
 };

 // Auto-save function with debouncing
 const autoSave = useCallback(() => {
 // Auto-save everything EXCEPT status changes (status uses explicit Save button)
 if (hasUnsavedChanges && !saving && orderData && formData.status === orderData.status) {
 handleSave();
 }
 }, [hasUnsavedChanges, saving, orderData, formData.status]);

 // Watch formData changes and trigger auto-save
 useEffect(() => {
   // Do not auto-save while a status change is pending (status uses explicit Save)
   if (hasUnsavedChanges && orderData && formData.status === orderData.status) {
    if (datePropagationModalOpen) return;
     // Clear existing timeout
     if (autoSaveTimeout) {
       clearTimeout(autoSaveTimeout);
     }

     // Set new timeout for auto-save (2 seconds after last change)
     const timeout = setTimeout(() => {
       autoSave();
     }, 2000);

     setAutoSaveTimeout(timeout);

     return () => clearTimeout(timeout);
   }
 }, [formData, hasUnsavedChanges, orderData, datePropagationModalOpen]); // Removed autoSaveTimeout and autoSave from dependencies

 // Warn user before closing tab/window with unsaved changes
 useEffect(() => {
 const handleBeforeUnload = (e) => {
 if (hasUnsavedChanges) {
 e.preventDefault();
 e.returnValue = '';
 return '';
 }
 };
 
 window.addEventListener('beforeunload', handleBeforeUnload);
 return () => window.removeEventListener('beforeunload', handleBeforeUnload);
 }, [hasUnsavedChanges]);

 const handleRecalculate = () => {
 // Recalculate is automatic via useMemo, just show confirmation
 toast.success(`Recalculated: ${formatCurrency(calculatedTotals.orderValue)}`);
 };

const canSyncDailyStatus = ['owner', 'admin', 'manager', 'staff'].includes((user?.role || '').toLowerCase());

const handleSyncToDailyStatus = async () => {
 if (!orderId) {
  toast.error('Order not found');
  return;
 }

 try {
  setSyncingDailyStatus(true);
  const response = await syncOrderToDailyStatus(orderId);
  const result = response?.data?.result || {};
  const daily = result.daily_status || {};
  const fabric = result.new_fabric_order || {};
  toast.success(
   `Daily Status sync complete • DAILY STATUS +${daily.rows_added || 0} (${daily.rows_skipped || 0} skipped), NEW FABRIC ORDER +${fabric.rows_added || 0} (${fabric.rows_skipped || 0} skipped)`
  );
 } catch (error) {
  console.error('Failed to sync order to Daily Status:', error);
  toast.error(error?.response?.data?.detail || 'Failed to sync order to Daily Status');
 } finally {
  setSyncingDailyStatus(false);
 }
};

 // Line Item Handlers
 const handleSelectProductForLineItem = async (product) => {
await fetchProducts();
fetchFactories();
 setSelectedProduct(product);
 setLineItemQuantity(1);
 setBasePrice(product.base_price); // Initialize with product's default price
setLineItemFactorySelection('__none__');
 try {
 const response = await getProductWithCustomizations(product.product_id);
 setProductCustomizations(response.data);
 
 // Pre-populate customization values with defaults from the product
 const defaultValues = {};
 response.data?.categories?.forEach(category => {
 category.options?.forEach(option => {
 if (option.default_value) {
 defaultValues[option.option_id] = option.default_value;
 }
 });
 });
 setCustomizationValues(defaultValues);
 } catch (error) {
 setProductCustomizations({ categories: [] });
 setCustomizationValues({});
 }
 setAddLineItemOpen(false);
 setAddLineItemCustomizationOpen(true);
 };

 // Legacy function for backwards compatibility
 const handleAddLineItem = async (product) => {
 await handleSelectProductForLineItem(product);
setAddLineItemOpen(false);
 };

 const handleConfirmAddLineItem = async () => {
 if (!selectedProduct) return;

 // Use custom base price if set, otherwise use product's base price
const finalBasePrice = basePrice ?? selectedProduct.base_price;

 // Snapshot customization data (capturing full option details for historical record)
 const customizations = Object.entries(customizationValues)
 .filter(([_, value]) => value && value !== '__none__')
 .map(([optionId, value]) => {
 const option = productCustomizations?.categories
 ?.flatMap(c => c.options || [])
 ?.find(o => o.option_id === optionId);
 const category = productCustomizations?.categories?.find(c => c.options?.some(o => o.option_id === optionId));
 return {
 option_id: optionId,
 option_name: option?.name || '',
 category_name: category?.name || '',
 category_id: category?.category_id || '',
 selected_value: value,
 price_impact: option?.price_impact || 0,
 // Snapshot data for historical preservation
 snapshot_data: {
 option_values: option?.option_values || [],
 captured_at: new Date().toISOString(),
 }
 };
 });

 const priceImpact = customizations.reduce((sum, c) => sum + (c.price_impact || 0), 0);
 const lineItemPrice = finalBasePrice + priceImpact;

 // Generate a proper line item ID (will be replaced on save if temp)
 const lineItemId = `LI-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`;

 const newItem = createLineItemWithSnapshot({
   lineItemId,
   productId: selectedProduct.product_id,
   productName: selectedProduct.name,
   garmentType: selectedProduct.default_garment_type || selectedProduct.name.toLowerCase(),
   quantity: lineItemQuantity,
   basePrice: finalBasePrice,
   lineItemPrice: lineItemPrice,
   customizationPriceImpact: priceImpact,
   customizations,
   productCustomizations,
  productionFactoryId: lineItemFactorySelection === '__none__' ? null : lineItemFactorySelection,
   isTest: false
 });

 // Update local state
 const updatedLineItems = [...lineItems, newItem];
 setLineItems(updatedLineItems);
setAddLineItemCustomizationOpen(false);
 setSelectedProduct(null);
 setProductCustomizations(null);
 setCustomizationValues({});
 setBasePrice(null);
 
 // Auto-save the order; backend will auto-create the required garment(s)
 try {
 setSaving(true);
 const itemsWithRefs = generateLineItemRefs(updatedLineItems, products, orderData.order_number);

 await updateOrder(orderId, {
 customer_id: formData.customer_id || orderData.customer_id,
order_date: formData.order_date || null,
status: formData.status,
 client_final_date: formData.client_final_date || null,
 internal_delivery_date: formData.internal_delivery_date || null,
 test_fit_date: formData.test_fit_date || null,
 store: formData.store === '__none__' ? null : formData.store,
 notes: formData.notes,
 gst_percentage: parseFloat(formData.gst_percentage) || 0,
 discount_type: formData.discount_type === '__none__' ? null : formData.discount_type,
 discount_value: parseFloat(formData.discount_value) || 0,
 line_items: itemsWithRefs,
 });

 const garmentLabel = lineItemQuantity === 1 ? 'garment' : `${lineItemQuantity} garments`;
 toast.success(`Line item added with ${garmentLabel}`);
 setHasUnsavedChanges(false);
 fetchOrderData();
 } catch (error) {
 console.error('Error saving line item:', error);
 toast.error('Failed to save line item. Please try saving manually.');
 setHasUnsavedChanges(true);
 } finally {
 setSaving(false);
 }
 };

 const handleCopyFromOrder = async (sourceLineItems, sourceOrder) => {
 const copiedItems = buildCopiedLineItems(sourceLineItems, { mode: 'edit' });
 if (copiedItems.length === 0) {
 toast.error('No line items found in the selected order');
 return;
 }

 const updatedLineItems = [...lineItems, ...copiedItems];
 setLineItems(updatedLineItems);

 try {
 setSaving(true);
 const itemsWithRefs = generateLineItemRefs(updatedLineItems, products, orderData.order_number);

 await updateOrder(orderId, {
 customer_id: formData.customer_id || orderData.customer_id,
order_date: formData.order_date || null,
 status: formData.status,
 client_final_date: formData.client_final_date || null,
 internal_delivery_date: formData.internal_delivery_date || null,
 test_fit_date: formData.test_fit_date || null,
 store: formData.store === '__none__' ? null : formData.store,
 notes: formData.notes,
 gst_percentage: parseFloat(formData.gst_percentage) || 0,
 discount_type: formData.discount_type === '__none__' ? null : formData.discount_type,
 discount_value: parseFloat(formData.discount_value) || 0,
 line_items: itemsWithRefs,
 });

 toast.success(`Copied ${copiedItems.length} item(s) from ${sourceOrder?.order_number || 'order'}`);
 setHasUnsavedChanges(false);
 fetchOrderData();
 } catch (error) {
 console.error('Error copying line items:', error);
 toast.error('Items were added but failed to save. Please save the order.');
 setHasUnsavedChanges(true);
 }
 finally {
 setSaving(false);
 }
 };

 // ============== TEST GARMENT HANDLERS ==============
 const testProducts = products.filter(p => p.name.startsWith('Test'));
 
 // Get compatible main garments for a test product (e.g., Test Jacket can mimic Jacket)
 const getCompatibleGarments = (testProduct) => {
 if (!testProduct?.shared_customization_product_id) return [];
 const mainProduct = products.find(p => p.product_id === testProduct.shared_customization_product_id);
 if (!mainProduct) return [];
 
 // Find garments in order that match the main product type
 const mainType = mainProduct.name.toLowerCase();
 return orderData?.garments?.filter(g => 
 g.garment_type?.toLowerCase() === mainType && !g.is_test_garment
 ) || [];
 };

 const handleOpenTestGarmentWizard = () => {
 fetchProducts();
 fetchFactories();
 setTestGarmentStep(1);
 setSelectedTestProduct(null);
 setTestGarmentMode('blank');
 setMimicSourceGarment(null);
setTestGarmentFactorySelection('__none__');
 setMimicCategories([]);
 setSourceCustomizations(null);
 setTestGarmentCustomValues({});
setQuickMultiMimicSelection({});
setQuickMultiMimicSelectedCategories([]);
setTestPlannerCounts({});
setTestPlannerQueue([]);
setTestPlannerCurrentIndex(0);
 setTestGarmentOpen(true);
 };

const getQuickMultiMimicGroups = useCallback(() => {
 const groups = {
  jacket: [],
  trouser: [],
  shirt: [],
  waistcoat: [],
 };

 const detectBaseType = (text = '') => {
  const normalized = String(text).toLowerCase();
  if (normalized.includes('jacket')) return 'jacket';
  if (normalized.includes('trouser') || normalized.includes('pant')) return 'trouser';
  if (normalized.includes('shirt')) return 'shirt';
  if (normalized.includes('waistcoat') || normalized.includes('vest')) return 'waistcoat';
  return null;
 };

 lineItemsWithRefs.forEach((item) => {
  if (isTestLineItem(item)) return;

  const baseType = detectBaseType(item.garment_type || item.product_name);
  if (!baseType) return;

  const linkedGarments = (orderData?.garments || []).filter((garment) => garment.line_item_id === item.line_item_id);
  const hasMainGarment = linkedGarments.some((garment) => !garment.is_test_garment);
  if (!hasMainGarment) return;

  const sourceGarment = linkedGarments.find((garment) => !garment.is_test_garment) || null;
  groups[baseType].push({
   ...item,
   source_garment_id: sourceGarment?.garment_id || null,
   source_garment_no: sourceGarment?.garment_no || item.line_item_ref || item.line_item_id,
   source_garment_type: sourceGarment?.garment_type || item.garment_type,
  });
 });

 return groups;
}, [lineItemsWithRefs, orderData?.garments]);

const getSuggestedTestPlannerCounts = useCallback(() => {
 const groups = getQuickMultiMimicGroups();
 return {
  jacket: groups.jacket?.length ? 1 : 0,
  trouser: groups.trouser?.length ? 1 : 0,
  shirt: groups.shirt?.length ? 1 : 0,
  waistcoat: groups.waistcoat?.length ? 1 : 0,
 };
}, [getQuickMultiMimicGroups]);

const handlePlannerCountChange = (typeKey, delta) => {
 setTestPlannerCounts((prev) => {
  const nextValue = Math.max(0, (prev[typeKey] || 0) + delta);
  return { ...prev, [typeKey]: nextValue };
 });
};

const initializePlannerQueue = async () => {
 const labels = { jacket: 'Jacket', trouser: 'Trouser', shirt: 'Shirt', waistcoat: 'Waistcoat' };
 const effectiveCounts = {
  ...getSuggestedTestPlannerCounts(),
  ...testPlannerCounts,
 };
 const counts = Object.entries(effectiveCounts).flatMap(([typeKey, count]) =>
  Array.from({ length: Math.max(0, Number(count) || 0) }).map((_, idx) => ({ typeKey, label: labels[typeKey], slot: idx + 1 }))
 );

 if (!counts.length) {
  toast.error('Please add at least one test garment in planner');
  return;
 }

 const queue = [];
 for (const entry of counts) {
  const typeText = entry.label;
  const testProduct = testProducts.find((p) => p.name.toLowerCase() === `test ${typeText.toLowerCase()}`);
  if (!testProduct) {
   toast.error(`Missing Test ${typeText} product in catalog`);
   return;
  }

  try {
   const resp = await getProductWithCustomizations(testProduct.product_id);
   const categories = resp?.data?.categories || [];
   queue.push({
    ...entry,
    testProduct,
    productCustomizations: resp.data,
    categoryIds: categories.map((c) => c.category_id),
    sourceGarment: null,
    sourceCustomizations: [],
    customValues: {},
    completed: false,
   });
  } catch (error) {
   toast.error(`Failed loading customizations for ${testProduct.name}`);
   return;
  }
 }

 setTestPlannerQueue(queue);
 setTestPlannerCurrentIndex(0);
 setTestPlannerSubStep('source');
 testPlannerSourceAutoPickRef.current = {};
 setTestGarmentStep(2);
};

const currentPlannedItem = testPlannerQueue[testPlannerCurrentIndex] || null;

const updateCurrentPlannedItem = (patch) => {
 setTestPlannerQueue((prev) => prev.map((item, idx) => (
  idx === testPlannerCurrentIndex ? { ...item, ...patch } : item
 )));
};

const handlePlannerSelectSource = (garment) => {
 if (!currentPlannedItem) return;
 const sourceLineItemId = garment.line_item_id;
 const sourceLineItem = lineItems.find((li) => li.line_item_id === sourceLineItemId);
 const categories = currentPlannedItem.productCustomizations?.categories || [];
 const schemaOptionMetaById = new Map();
 categories.forEach((category) => {
  (category.options || []).forEach((option) => {
   schemaOptionMetaById.set(option.option_id, { option, category });
  });
 });
 const categoryNames = categories
  .filter((c) => (currentPlannedItem.categoryIds || []).includes(c.category_id))
  .map((c) => (c.name || '').toLowerCase());

 const initialValues = {};
 const sourceCustoms = sourceLineItem?.customizations || [];
 sourceCustoms.forEach((c) => {
  const matchesCategory = (c.category_id && (currentPlannedItem.categoryIds || []).includes(c.category_id)) ||
   (c.category_name && categoryNames.includes(c.category_name.toLowerCase()));
  if (matchesCategory && c.option_id) {
   const optionMeta = schemaOptionMetaById.get(c.option_id);
   const useTestFabric = isMainOrLiningFabricOption(optionMeta?.option, c.option_name);
   initialValues[c.option_id] = useTestFabric ? 'TEST FABRIC' : c.selected_value;
  }
 });

 updateCurrentPlannedItem({
  sourceGarment: {
   garment_id: garment.garment_id || garment.source_garment_id || sourceLineItemId,
   garment_no: garment.garment_no || garment.source_garment_no || garment.line_item_ref || sourceLineItemId,
   garment_type: garment.garment_type || garment.source_garment_type || sourceLineItem?.garment_type,
   line_item_id: sourceLineItemId,
  },
  sourceCustomizations: sourceCustoms,
  customValues: initialValues,
 });
};

const handlePlannerToggleCategory = (categoryId) => {
 if (!currentPlannedItem) return;
 const current = currentPlannedItem.categoryIds || [];
 const next = current.includes(categoryId)
  ? current.filter((id) => id !== categoryId)
  : [...current, categoryId];
 updateCurrentPlannedItem({ categoryIds: next });
};

const getPlannerItemProgress = (item) => {
 const hasSource = Boolean(item?.sourceGarment);
 const hasCategories = ((item?.productCustomizations?.categories?.length || 0) === 0) || Boolean((item?.categoryIds || []).length > 0);
 const hasOptions = true;
 const ready = hasSource && hasCategories && hasOptions;

 if (ready) return { label: 'Ready', color: 'text-green-700 bg-green-50 border-green-200' };
 if (hasSource || hasCategories) return { label: 'In Progress', color: 'text-amber-700 bg-amber-50 border-amber-200' };
 return { label: 'Pending', color: 'text-slate-600 bg-slate-50 border-slate-200' };
};

const handlePlannerCustomValueChange = (optionId, value) => {
 if (!currentPlannedItem) return;
 updateCurrentPlannedItem({
  customValues: {
   ...(currentPlannedItem.customValues || {}),
   [optionId]: value,
  },
 });
};

const handlePlannerNextItem = () => {
 if (!currentPlannedItem) return;

 if (testPlannerSubStep === 'source') {
  if (!currentPlannedItem.sourceGarment) {
   toast.error('Please select a source garment first');
   return;
  }
  setTestPlannerSubStep('categories');
  return;
 }

 if (testPlannerSubStep === 'categories') {
  if ((currentPlannedItem.productCustomizations?.categories?.length || 0) > 0 && (currentPlannedItem.categoryIds || []).length === 0) {
   toast.error('Please select at least one category');
   return;
  }
  setTestPlannerSubStep('options');
  return;
 }

 setTestPlannerQueue((prev) => prev.map((item, idx) => idx === testPlannerCurrentIndex ? { ...item, completed: true } : item));
 if (testPlannerCurrentIndex < testPlannerQueue.length - 1) {
  setTestPlannerCurrentIndex((i) => i + 1);
  setTestPlannerSubStep('source');
 } else {
  setTestGarmentStep(3);
 }
};

const handlePlannerPrevItem = () => {
 if (testPlannerSubStep === 'options') {
  setTestPlannerSubStep('categories');
  return;
 }
 if (testPlannerSubStep === 'categories') {
  setTestPlannerSubStep('source');
  return;
 }
 if (testPlannerCurrentIndex > 0) {
  setTestPlannerCurrentIndex((i) => i - 1);
  setTestPlannerSubStep('options');
 }
};

const createPlannedTestGarment = async (plannedItem) => {
 const lineItemId = `LI-TEST-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`;
 const categories = plannedItem.productCustomizations?.categories || [];
 const customizations = [];
 categories
  .filter((c) => (plannedItem.categoryIds || []).includes(c.category_id))
  .forEach((category) => {
   (category.options || []).forEach((option) => {
    const value = plannedItem.customValues?.[option.option_id] || '';
    customizations.push({
     option_id: option.option_id,
     option_name: option.name,
     category_name: category.name,
     category_id: category.category_id,
     selected_value: value,
     price_impact: 0,
    });
   });
  });

 const sourceLineItem = lineItems.find((li) => li.line_item_id === plannedItem.sourceGarment?.line_item_id);
 const sourceFactoryId = sourceLineItem?.production_factory_id || null;

 const newItem = createLineItemWithSnapshot({
  lineItemId,
  productId: plannedItem.testProduct.product_id,
  productName: plannedItem.testProduct.name,
  customizations,
  productCustomizations: plannedItem.productCustomizations,
  productionFactoryId: sourceFactoryId,
  isTest: true,
 });

 return { newItem, lineItemId };
};

const handleCreateAllPlannedTestGarments = async () => {
 if (!testPlannerQueue.length) return;
 const duplicateSourceIds = Object.values(
  testPlannerQueue.reduce((acc, item) => {
   const key = item.sourceGarment?.garment_id;
   if (!key) return acc;
   acc[key] = (acc[key] || 0) + 1;
   return acc;
  }, {})
 ).some((count) => count > 1);

 if (duplicateSourceIds) {
  toast.warning('Same source garment selected multiple times. Proceeding as requested.');
 }

 try {
  setTestPlannerSubmitting(true);
  let workingLineItems = [...lineItems];
  const createdItemsMeta = [];

  for (const plannedItem of testPlannerQueue) {
   if (!plannedItem.sourceGarment) {
    throw new Error(`Missing source garment for ${plannedItem.testProduct?.name}`);
   }
   const { newItem, lineItemId } = await createPlannedTestGarment(plannedItem);
   workingLineItems = [...workingLineItems, newItem];
   createdItemsMeta.push({ plannedItem, lineItemId });
  }

  const itemsWithRefs = generateLineItemRefs(workingLineItems, products, orderData.order_number);
  const savedOrder = await updateOrder(orderId, {
   customer_id: formData.customer_id || orderData.customer_id,
order_date: formData.order_date || null,
  status: formData.status,
   client_final_date: formData.client_final_date || null,
   internal_delivery_date: formData.internal_delivery_date || null,
   test_fit_date: formData.test_fit_date || null,
   store: formData.store === '__none__' ? null : formData.store,
   notes: formData.notes,
   gst_percentage: parseFloat(formData.gst_percentage) || 0,
   discount_type: formData.discount_type === '__none__' ? null : formData.discount_type,
   discount_value: parseFloat(formData.discount_value) || 0,
   line_items: itemsWithRefs,
  });

  for (const itemMeta of createdItemsMeta) {
   const savedLineItem = savedOrder.data?.line_items?.find(
    (li) => li.line_item_id === itemMeta.lineItemId
   );
   const finalLineItemId = savedLineItem?.line_item_id || itemMeta.lineItemId;
   const baseType = itemMeta.plannedItem.testProduct.name.replace('Test ', '').toLowerCase();
   const garmentType = `test_${baseType}`;
   await createGarment({
    order_id: orderId,
    line_item_id: finalLineItemId,
    garment_type: garmentType,
    production_factory_id: savedLineItem?.production_factory_id || null,
    garment_status: 'not_started',
    priority: 'normal',
    is_test_garment: true,
    notes: `Test garment mimicking ${itemMeta.plannedItem.sourceGarment?.garment_no || 'source garment'}`,
   });
  }

  toast.success(`Created ${createdItemsMeta.length} planned test garment(s)`);
  setLineItemViewMode('test');
  setGarmentViewMode('test');
  setTestGarmentOpen(false);
  fetchOrderData();
 } catch (error) {
  console.error('Error creating planned test garments:', error);
  toast.error(error?.message || 'Failed to create planned test garments');
 } finally {
  setTestPlannerSubmitting(false);
 }
};

const toggleQuickMultiCategory = (categoryId) => {
 setQuickMultiMimicSelectedCategories((prev) => (
  prev.includes(categoryId)
   ? prev.filter((id) => id !== categoryId)
   : [...prev, categoryId]
 ));
};

const handleQuickMultiMimicSelection = (baseType, lineItemId) => {
 setQuickMultiMimicSelection((prev) => ({
  ...prev,
  [baseType]: lineItemId,
 }));
};

const handleCreateQuickMultiMimic = async () => {
 const selectedLineItemIds = Object.values(quickMultiMimicSelection).filter(Boolean);
 if (!selectedLineItemIds.length) {
  toast.error('Choose at least one source garment');
  return;
 }

 if ((productCustomizations?.categories?.length || 0) > 0 && quickMultiMimicSelectedCategories.length === 0) {
  toast.error('Select at least one category');
  return;
 }

 try {
  setQuickMultiMimicSubmitting(true);
  const selectedCategoryNames = (productCustomizations?.categories || [])
   .filter((category) => quickMultiMimicSelectedCategories.includes(category.category_id))
   .map((category) => category.name)
   .filter(Boolean);

  const response = await bulkCreateTestGarments(orderId, {
   line_item_ids: selectedLineItemIds,
   selected_category_ids: quickMultiMimicSelectedCategories,
   selected_category_names: selectedCategoryNames,
  });

  const createdCount = response?.data?.created_count || 0;
  const skippedCount = response?.data?.skipped_count || 0;
  if (createdCount > 0) {
   toast.success(`Created ${createdCount} test garment(s)${skippedCount ? `, skipped ${skippedCount}` : ''}`);
   setLineItemViewMode('test');
   setGarmentViewMode('test');
   setTestGarmentOpen(false);
   setQuickMultiMimicSelection({});
   setQuickMultiMimicSelectedCategories([]);
   await fetchOrderData({ silent: true });
  } else {
   toast.error('No test garments were created');
  }
 } catch (error) {
  console.error('Failed to create quick multi-mimic test garments:', error);
  const detail = error?.response?.data?.detail;
  toast.error(detail || 'Failed to create test garments');
 } finally {
  setQuickMultiMimicSubmitting(false);
 }
};

 const handleSelectTestProduct = async (product) => {
 setSelectedTestProduct(product);
 try {
 const response = await getProductWithCustomizations(product.product_id);
 setProductCustomizations(response.data);
 // Pre-select all categories for mimic mode
 const categoryIds = response.data?.categories?.map(c => c.category_id) || [];
 setMimicCategories(categoryIds);
 } catch (error) {
 setProductCustomizations({ categories: [] });
 }
 setTestGarmentStep(2);
 };

 const handleSelectMimicMode = async (mode) => {
 setTestGarmentMode(mode);
 if (mode === 'blank') {
 // For blank mode, go to step 3 to configure customizations (same as mimic but without source)
 setMimicSourceGarment(null);
setTestGarmentFactorySelection('__none__');
 setTestGarmentStep(3);
} else if (mode === 'quick_multi_mimic') {
 setMimicSourceGarment(null);
 setTestGarmentFactorySelection('__none__');
 setQuickMultiMimicSelection({});
 setQuickMultiMimicSelectedCategories([]);
 setTestGarmentStep(3);
 } else {
 // Move to step 3 to select source garment and categories
 setTestGarmentStep(3);
 }
 };

 const handleSelectMimicSource = async (garment) => {
 setMimicSourceGarment(garment);
 // Load the source garment's line item customizations
 const sourceLineItem = lineItems.find(li => li.line_item_id === garment.line_item_id);
 if (sourceLineItem) {
 setSourceCustomizations(sourceLineItem.customizations || []);
setTestGarmentFactorySelection(sourceLineItem.production_factory_id || '__none__');
 }
 };

 const toggleMimicCategory = (categoryId) => {
 setMimicCategories(prev => 
 prev.includes(categoryId) 
 ? prev.filter(id => id !== categoryId)
 : [...prev, categoryId]
 );
 };

 // Proceed to Step 4 (Configure Options) with pre-populated values
 const handleProceedToConfigureOptions = () => {
 const initialValues = {};
 
 if (testGarmentMode === 'mimic' && mimicSourceGarment) {
 // Pre-populate with source garment's values
 const sourceLineItem = lineItems.find(li => li.line_item_id === mimicSourceGarment.line_item_id);
 const sourceCustoms = sourceLineItem?.customizations || [];

 const schemaOptionMetaById = new Map();
 productCustomizations?.categories?.forEach(category => {
   (category.options || []).forEach(option => {
     schemaOptionMetaById.set(option.option_id, { option, category });
   });
 });
 
 // Get category names to copy
 const categoryNames = productCustomizations?.categories
 ?.filter(c => mimicCategories.includes(c.category_id))
 ?.map(c => c.name.toLowerCase()) || [];
 
 sourceCustoms.forEach(c => {
 const matchesCategory = (c.category_id && mimicCategories.includes(c.category_id)) ||
 (c.category_name && categoryNames.includes(c.category_name.toLowerCase()));
 
 if (matchesCategory && c.option_id) {
const optionMeta = schemaOptionMetaById.get(c.option_id);
const useTestFabric = isMainOrLiningFabricOption(optionMeta?.option, c.option_name);
initialValues[c.option_id] = useTestFabric ? 'TEST FABRIC' : c.selected_value;
 }
 });
 } else {
// Blank mode - pre-populate with defaults; force TEST FABRIC only for main/lining fabric IDs
 productCustomizations?.categories
 ?.filter(c => mimicCategories.includes(c.category_id))
 ?.forEach(category => {
 category.options?.forEach(option => {
const useTestFabric = isMainOrLiningFabricOption(option);
initialValues[option.option_id] = useTestFabric ? 'TEST FABRIC' : (option.default_value || '');
 });
 });
 }
 
 setTestGarmentCustomValues(initialValues);
 setTestGarmentStep(4);
 };

 const handleCreateTestGarment = async () => {
 if (!selectedTestProduct) return;
 
 try {
 setSaving(true);
 const lineItemId = `LI-TEST-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`;
 
 // Build customizations from the configured values
 let customizations = [];
 
 productCustomizations?.categories
 ?.filter(c => mimicCategories.includes(c.category_id))
 ?.forEach(category => {
 category.options?.forEach(option => {
 const value = testGarmentCustomValues[option.option_id] || '';
 customizations.push({
 option_id: option.option_id,
 option_name: option.name,
 category_name: category.name,
 category_id: category.category_id,
 selected_value: value,
 price_impact: 0, // No price impact for test garments
 });
 });
 });
 
 // Create line item with zero price
 const sourceFactoryId =
  testGarmentFactorySelection === '__none__'
   ? null
   : testGarmentFactorySelection;

 const newItem = createLineItemWithSnapshot({
   lineItemId,
   productId: selectedTestProduct.product_id,
   productName: selectedTestProduct.name,
   customizations,
   productCustomizations,
  productionFactoryId: sourceFactoryId,
   isTest: true
 });

 const updatedLineItems = [...lineItems, newItem];
 setLineItems(updatedLineItems);
 
 // Save order and create garment
 const itemsWithRefs = generateLineItemRefs(updatedLineItems, products, orderData.order_number);
  const newTestItemWithRef = itemsWithRefs.find(li => li.line_item_id === lineItemId);
 
  const savedOrder = await updateOrder(orderId, {
 customer_id: formData.customer_id || orderData.customer_id,
order_date: formData.order_date || null,
status: formData.status,
 client_final_date: formData.client_final_date || null,
 internal_delivery_date: formData.internal_delivery_date || null,
 test_fit_date: formData.test_fit_date || null,
 store: formData.store === '__none__' ? null : formData.store,
 notes: formData.notes,
 gst_percentage: parseFloat(formData.gst_percentage) || 0,
 discount_type: formData.discount_type === '__none__' ? null : formData.discount_type,
 discount_value: parseFloat(formData.discount_value) || 0,
 line_items: itemsWithRefs,
 });

  const savedLineItem = savedOrder.data?.line_items?.find(
    li => li.line_item_id === lineItemId || li.line_item_ref === newTestItemWithRef?.line_item_ref
  );
  const finalLineItemId = savedLineItem?.line_item_id || lineItemId;
 
 // Create test garment with type that includes "test_" prefix
 const baseType = selectedTestProduct.name.replace('Test ', '').toLowerCase();
 const garmentType = `test_${baseType}`;
 
 await createGarment({
 order_id: orderId,
  line_item_id: finalLineItemId,
 garment_type: garmentType,
 production_factory_id: newItem.production_factory_id || null,
 garment_status: 'not_started',
 priority: 'normal',
 is_test_garment: true,
 notes: testGarmentMode === 'mimic' && mimicSourceGarment 
 ? `Test garment mimicking ${mimicSourceGarment.garment_no}` 
 : 'Test garment (blank)',
 });
 
 toast.success(`Test ${baseType} added successfully`);
 setLineItemViewMode('test');
 setGarmentViewMode('test');
 setTestGarmentOpen(false);
 setHasUnsavedChanges(false);
 fetchOrderData();
 } catch (error) {
 console.error('Error creating test garment:', error);
 toast.error('Failed to create test garment');
 } finally {
 setSaving(false);
 }
 };

 const handleEditLineItem = async (item) => {
 const productCatalog = (await fetchProducts()) || products;
 await fetchFactories();
 setEditingLineItem(item);
 const product = productCatalog.find(p => p.product_id === item.product_id);
 setSelectedProduct(product);
 setLineItemQuantity(item.quantity);
 setBasePrice(item.base_price); // Set the base price from the item
setLineItemFactorySelection(item.production_factory_id || '__none__');
 
 const values = {};
 (item.customizations || []).forEach(c => {
 values[c.option_id] = c.selected_value;
 });
 setCustomizationValues(values);

 try {
 // Use snapshot if it exists (for edited line items), otherwise fetch master schema
 if (item.snapshot_customization_schema) {
 // Line item has its own snapshot - use that
 setProductCustomizations(item.snapshot_customization_schema);
 } else {
 // No snapshot yet - fetch master product schema
 const response = await getProductWithCustomizations(item.product_id);
 setProductCustomizations(response.data);
 }
 } catch (error) {
 setProductCustomizations({ categories: [] });
 }
 setEditLineItemOpen(true);
 };

 const handleConfirmEditLineItem = async () => {
 if (!editingLineItem || !selectedProduct) return;

 const customizations = Object.entries(customizationValues)
 .filter(([_, value]) => value && value !== '__none__')
 .map(([optionId, value]) => {
 const option = productCustomizations?.categories
 ?.flatMap(c => c.options || [])
 ?.find(o => o.option_id === optionId);
 return {
 option_id: optionId,
 option_name: option?.name || '',
 category_name: productCustomizations?.categories?.find(c => c.options?.some(o => o.option_id === optionId))?.name || '',
 selected_value: value,
 price_impact: option?.price_impact || 0
 };
 });

 const priceImpact = customizations.reduce((sum, c) => sum + (c.price_impact || 0), 0);
const finalBasePrice = basePrice ?? selectedProduct.base_price;
 const lineItemPrice = finalBasePrice + priceImpact;

 const updatedLineItems = lineItems.map(item => 
 item.line_item_id === editingLineItem.line_item_id
 ? { 
 ...item, 
 quantity: lineItemQuantity,
 production_factory_id: lineItemFactorySelection === '__none__' ? null : lineItemFactorySelection,
 dispatch_factory_id: lineItemFactorySelection === '__none__' ? null : lineItemFactorySelection,
 base_price: finalBasePrice,
 customizations, 
 customization_price_impact: priceImpact,
 line_item_price: lineItemPrice,
 snapshot_customization_schema: productCustomizations // Save the current schema as snapshot
 }
 : item
 );

let previewData = null;
try {
 const previewResponse = await previewLineItemFabricChange(orderId, editingLineItem.line_item_id, {
 old_customizations: editingLineItem.customizations || [],
 new_customizations: customizations,
 });
 previewData = previewResponse.data;
} catch (previewError) {
 console.warn('Fabric sync preview unavailable; proceeding without prompt', previewError);
}

setEditLineItemOpen(false);
setEditingLineItem(null);
setSelectedProduct(null);
setProductCustomizations(null);
setCustomizationValues({});

if (previewData?.requires_confirmation && (previewData?.garments || []).length > 0) {
 const initialSelections = {};
 (previewData.garments || []).forEach(garment => {
 initialSelections[garment.garment_id] = garment.default_decision || 'update_existing';
 });

 setFabricSyncPreview(previewData);
 setFabricSyncSelections(initialSelections);
 setPendingLineItemSaveContext({
 updatedLineItems,
 lineItemId: editingLineItem.line_item_id,
 oldCustomizations: editingLineItem.customizations || [],
 newCustomizations: customizations,
 });
 setFabricSyncDialogOpen(true);
 return;
}

await persistEditedLineItemChanges({
 updatedLineItems,
 lineItemId: editingLineItem.line_item_id,
 oldCustomizations: editingLineItem.customizations || [],
 newCustomizations: customizations,
 applyFabricSync: false,
});
 };

const handleFabricSyncDecisionChange = (garmentId, decision) => {
 setFabricSyncSelections(prev => ({
 ...prev,
 [garmentId]: decision,
 }));
};

const handleConfirmFabricSyncSave = async () => {
 if (!pendingLineItemSaveContext) {
   clearFabricSyncFlow();
   return;
 }

 const ok = await persistEditedLineItemChanges({
 ...pendingLineItemSaveContext,
 applyFabricSync: true,
 });

 if (ok) {
   clearFabricSyncFlow();
 }
};

const handleCancelFabricSyncSave = () => {
 clearFabricSyncFlow();
 toast.message('Line item fabric sync cancelled. No changes were saved.');
};

 const handleDuplicateLineItem = async (item) => {
 const productCatalog = (await fetchProducts()) || products;
 await fetchFactories();
 // Open a modal to allow editing customizations before duplicating
 setDuplicatingLineItem(item);
 const product = productCatalog.find(p => p.product_id === item.product_id);
 setSelectedProduct(product);
 setLineItemQuantity(item.quantity);
 setBasePrice(item.base_price);
setLineItemFactorySelection(item.production_factory_id || '__none__');
 
 // Pre-fill customization values from the original item
 const values = {};
 (item.customizations || []).forEach(c => {
 values[c.option_id] = c.selected_value;
 });
 setCustomizationValues(values);

 try {
 const response = await getProductWithCustomizations(item.product_id);
 setProductCustomizations(response.data);
 } catch (error) {
 setProductCustomizations({ categories: [] });
 }
 setDuplicateLineItemOpen(true);
 };

 const handleConfirmDuplicateLineItem = async () => {
 if (!duplicatingLineItem || !selectedProduct) return;

 const lineItemId = `LI-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`;
 
 const customizations = Object.entries(customizationValues)
 .filter(([_, value]) => value && value !== '__none__')
 .map(([optionId, value]) => {
 const option = productCustomizations?.categories
 ?.flatMap(c => c.options || [])
 ?.find(o => o.option_id === optionId);
 return {
 option_id: optionId,
 option_name: option?.name || '',
 category_name: productCustomizations?.categories?.find(c => c.options?.some(o => o.option_id === optionId))?.name || '',
 selected_value: value,
 price_impact: option?.price_impact || 0
 };
 });

const finalBasePrice = basePrice ?? selectedProduct.base_price;
 const priceImpact = customizations.reduce((sum, c) => sum + (c.price_impact || 0), 0);
 const lineItemPrice = finalBasePrice + priceImpact;

 const newItem = {
 line_item_id: lineItemId,
 product_id: selectedProduct.product_id,
 product_name: selectedProduct.name,
 garment_type: selectedProduct.default_garment_type || selectedProduct.name.toLowerCase(),
 production_factory_id: lineItemFactorySelection === '__none__' ? null : lineItemFactorySelection,
 dispatch_factory_id: lineItemFactorySelection === '__none__' ? null : lineItemFactorySelection,
 quantity: lineItemQuantity,
 base_price: finalBasePrice,
 customization_price_impact: priceImpact,
 line_item_price: lineItemPrice,
 customizations: customizations,
 snapshot_customization_schema: duplicatingLineItem.snapshot_customization_schema || productCustomizations, // Use original snapshot or current schema
 };
 
 const updatedLineItems = [...lineItems, newItem];
 setLineItems(updatedLineItems);
 
 // Close the modal and reset states
 setDuplicateLineItemOpen(false);
 setDuplicatingLineItem(null);
 setSelectedProduct(null);
 setProductCustomizations(null);
 setCustomizationValues({});
 setBasePrice(null);
 
 // Auto-save; backend will auto-create the required garment(s)
 try {
 setSaving(true);
 const itemsWithRefs = generateLineItemRefs(updatedLineItems, products, orderData.order_number);

 await updateOrder(orderId, {
 customer_id: formData.customer_id || orderData.customer_id,
order_date: formData.order_date || null,
status: formData.status,
 client_final_date: formData.client_final_date || null,
 internal_delivery_date: formData.internal_delivery_date || null,
 test_fit_date: formData.test_fit_date || null,
 store: formData.store === '__none__' ? null : formData.store,
 notes: formData.notes,
 gst_percentage: parseFloat(formData.gst_percentage) || 0,
 discount_type: formData.discount_type === '__none__' ? null : formData.discount_type,
 discount_value: parseFloat(formData.discount_value) || 0,
 line_items: itemsWithRefs,
 });

 const garmentLabel = lineItemQuantity === 1 ? 'garment' : `${lineItemQuantity} garments`;
 toast.success(`Line item duplicated with ${garmentLabel}`);
 setHasUnsavedChanges(false);
 fetchOrderData();
 } catch (error) {
 console.error('Error duplicating line item:', error);
 toast.error('Failed to duplicate. Please try saving manually.');
 setHasUnsavedChanges(true);
 } finally {
 setSaving(false);
 }
 };

 const handleLineItemPriceChange = (lineItemId, newPrice) => {
 setLineItems(lineItems.map(item => 
 item.line_item_id === lineItemId
 ? { ...item, line_item_price: newPrice }
 : item
 ));
 setHasUnsavedChanges(true);
 toast.success('Price updated');
 };

 const handleBasePriceChange = (lineItemId, newBasePrice) => {
 setLineItems(lineItems.map(item => {
 if (item.line_item_id === lineItemId) {
 // Recalculate line item price: base price + customization impact
 const customizationImpact = item.customization_price_impact || 0;
 return { 
 ...item, 
 base_price: newBasePrice,
 line_item_price: newBasePrice + customizationImpact
 };
 }
 return item;
 }));
 setHasUnsavedChanges(true);
 toast.success('Base price updated');
 };

 const handleDeleteLineItem = (item) => {
const getSafelyLinkedGarmentsForLineItem = (lineItem) => {
const garments = orderData.garments || [];
const byLineItemId = garments.filter(g => g.line_item_id === lineItem.line_item_id);
if (byLineItemId.length > 0) return byLineItemId;

const refSuffix = lineItem.line_item_ref?.split('-').pop();
if (!refSuffix) return [];

const lineItemsWithSameSuffix = lineItemsWithRefs.filter(
li => li.line_item_ref?.split('-').pop() === refSuffix
);
if (lineItemsWithSameSuffix.length !== 1) return [];

const bySuffix = garments.filter(g => {
const garmentLineItem = lineItemsWithRefs.find(li => li.line_item_id === g.line_item_id);
const garmentRefSuffix = garmentLineItem?.line_item_ref?.split('-').pop();
return garmentRefSuffix === refSuffix;
});

return bySuffix.length === 1 ? bySuffix : [];
};

// Safely find linked garments/fabrics for this line item
const linkedGarments = getSafelyLinkedGarmentsForLineItem(item);
 
 const linkedGarmentIds = linkedGarments.map(g => g.garment_id);
 const linkedFabricOrders = orderData.fabric_orders?.filter(fo => linkedGarmentIds.includes(fo.garment_id)) || [];
 
 setItemToDelete({ 
 type: 'lineItem', 
 item,
 linkedGarments,
 linkedFabricOrders
 });
 setDeleteConfirmOpen(true);
 };

 const handleConfirmDelete = async (deleteLinkedItems = false) => {
 if (!itemToDelete) return;
 
 if (itemToDelete.type === 'lineItem') {
 try {
 setSaving(true);
 
 // If user chose to delete linked items
 if (deleteLinkedItems && itemToDelete.linkedGarments?.length > 0) {
 // Delete linked fabric orders first
 for (const fo of itemToDelete.linkedFabricOrders || []) {
 try {
 await deleteFabricOrder(fo.fabric_id);
 } catch (error) {
 console.error('Error deleting fabric order:', error);
 }
 }
 
 // Delete linked garments
 for (const garment of itemToDelete.linkedGarments) {
 try {
 await deleteGarment(garment.garment_id);
 } catch (error) {
 console.error('Error deleting garment:', error);
 }
 }
 }
 
 // Remove line item from local state
 const updatedLineItems = lineItems.filter(li => li.line_item_id !== itemToDelete.item.line_item_id);
 
 // Immediately save the order with the line item removed
 const itemsWithRefs = generateLineItemRefs(updatedLineItems, products, orderData.order_number);
 await updateOrder(orderId, {
 customer_id: formData.customer_id || orderData.customer_id,
order_date: formData.order_date || null,
status: formData.status,
 client_final_date: formData.client_final_date || null,
 internal_delivery_date: formData.internal_delivery_date || null,
 test_fit_date: formData.test_fit_date || null,
 store: formData.store === '__none__' ? null : formData.store,
 notes: formData.notes,
 gst_percentage: parseFloat(formData.gst_percentage) || 0,
 discount_type: formData.discount_type === '__none__' ? null : formData.discount_type,
 discount_value: parseFloat(formData.discount_value) || 0,
 line_items: itemsWithRefs,
 });
 
 toast.success('Line item deleted' + (deleteLinkedItems ? ' with linked garments/fabrics' : ''));
 
const linkedGarmentIds = new Set((itemToDelete.linkedGarments || []).map(g => g.garment_id));
const linkedFabricIds = new Set((itemToDelete.linkedFabricOrders || []).map(f => f.fabric_id));

setLineItems(updatedLineItems);
setOrderData(prev => prev ? ({
...prev,
line_items: itemsWithRefs,
garments: deleteLinkedItems
? (prev.garments || []).filter(g => !linkedGarmentIds.has(g.garment_id))
: prev.garments,
fabric_orders: deleteLinkedItems
? (prev.fabric_orders || []).filter(fo => !linkedFabricIds.has(fo.fabric_id) && !linkedGarmentIds.has(fo.garment_id))
: prev.fabric_orders,
}) : prev);

await fetchOrderData({ silent: true });
 } catch (error) {
 console.error('Error deleting line item:', error);
 toast.error('Failed to delete line item');
 } finally {
 setSaving(false);
 }
 } else if (itemToDelete.type === 'bulkLineItems') {
 try {
 setSaving(true);
 
 // If user chose to delete linked items
 if (deleteLinkedItems && itemToDelete.linkedGarments?.length > 0) {
 // Delete linked fabric orders first
 for (const fo of itemToDelete.linkedFabricOrders || []) {
 try {
 await deleteFabricOrder(fo.fabric_id);
 } catch (error) {
 console.error('Error deleting fabric order:', error);
 }
 }
 
 // Delete linked garments
 for (const garment of itemToDelete.linkedGarments) {
 try {
 await deleteGarment(garment.garment_id);
 } catch (error) {
 console.error('Error deleting garment:', error);
 }
 }
 }
 
 // Remove all selected line items from local state
 const selectedIds = itemToDelete.items.map(i => i.line_item_id);
 const updatedLineItems = lineItems.filter(li => !selectedIds.includes(li.line_item_id));
 
 // Immediately save the order with the line items removed
 const itemsWithRefs = generateLineItemRefs(updatedLineItems, products, orderData.order_number);
 await updateOrder(orderId, {
 customer_id: formData.customer_id || orderData.customer_id,
order_date: formData.order_date || null,
status: formData.status,
 client_final_date: formData.client_final_date || null,
 internal_delivery_date: formData.internal_delivery_date || null,
 test_fit_date: formData.test_fit_date || null,
 store: formData.store === '__none__' ? null : formData.store,
 notes: formData.notes,
 gst_percentage: parseFloat(formData.gst_percentage) || 0,
 discount_type: formData.discount_type === '__none__' ? null : formData.discount_type,
 discount_value: parseFloat(formData.discount_value) || 0,
 line_items: itemsWithRefs,
 });
 
 const count = itemToDelete.items.length;
 toast.success(`${count} item(s) deleted` + (deleteLinkedItems ? ' with linked garments/fabrics' : ''));
 
const linkedGarmentIds = new Set((itemToDelete.linkedGarments || []).map(g => g.garment_id));
const linkedFabricIds = new Set((itemToDelete.linkedFabricOrders || []).map(f => f.fabric_id));

setLineItems(updatedLineItems);
setOrderData(prev => prev ? ({
...prev,
line_items: itemsWithRefs,
garments: deleteLinkedItems
? (prev.garments || []).filter(g => !linkedGarmentIds.has(g.garment_id))
: prev.garments,
fabric_orders: deleteLinkedItems
? (prev.fabric_orders || []).filter(fo => !linkedFabricIds.has(fo.fabric_id) && !linkedGarmentIds.has(fo.garment_id))
: prev.fabric_orders,
}) : prev);

// Clear selection and refresh data silently
 setSelectedLineItems([]);
await fetchOrderData({ silent: true });
 } catch (error) {
 console.error('Error deleting line items:', error);
 toast.error('Failed to delete line items');
 } finally {
 setSaving(false);
 }
 }
 
 setDeleteConfirmOpen(false);
 setItemToDelete(null);
 };

 const handleSelectAllLineItems = (checked) => {
 const visibleIds = visibleLineItems.map((item) => item.line_item_id);
 if (checked) {
 setSelectedLineItems((prev) => [...new Set([...prev, ...visibleIds])]);
 } else {
 setSelectedLineItems((prev) => prev.filter((id) => !visibleIds.includes(id)));
 }
 };

 const handleDeleteSelectedLineItems = () => {
 if (selectedLineItems.length === 0) return;
 
 // Find all linked garments and fabric orders for selected line items
 const selectedItems = lineItems.filter(li => selectedLineItems.includes(li.line_item_id));
 
 let allLinkedGarments = [];
 let allLinkedFabricOrders = [];
 
 for (const item of selectedItems) {
const garments = orderData.garments || [];
const byLineItemId = garments.filter(g => g.line_item_id === item.line_item_id);

let linkedGarments = byLineItemId;
if (linkedGarments.length === 0) {
const refSuffix = item.line_item_ref?.split('-').pop();
if (refSuffix) {
const lineItemsWithSameSuffix = lineItemsWithRefs.filter(
li => li.line_item_ref?.split('-').pop() === refSuffix
);
if (lineItemsWithSameSuffix.length === 1) {
const bySuffix = garments.filter(g => {
const garmentLineItem = lineItemsWithRefs.find(li => li.line_item_id === g.line_item_id);
const garmentRefSuffix = garmentLineItem?.line_item_ref?.split('-').pop();
return garmentRefSuffix === refSuffix;
});
linkedGarments = bySuffix.length === 1 ? bySuffix : [];
}
}
}
 
 allLinkedGarments = [...allLinkedGarments, ...linkedGarments];
 }
 
 // Remove duplicates
 allLinkedGarments = allLinkedGarments.filter((g, idx, arr) => 
 arr.findIndex(x => x.garment_id === g.garment_id) === idx
 );
 
 const linkedGarmentIds = allLinkedGarments.map(g => g.garment_id);
 allLinkedFabricOrders = orderData.fabric_orders?.filter(fo => linkedGarmentIds.includes(fo.garment_id)) || [];
 
 setItemToDelete({ 
 type: 'bulkLineItems', 
 items: selectedItems,
 linkedGarments: allLinkedGarments,
 linkedFabricOrders: allLinkedFabricOrders
 });
 setDeleteConfirmOpen(true);
 };

 // Garment Handlers
 const handleGarmentStatusChange = async (garmentId, status) => {
 try {
 await updateGarmentStatus(garmentId, status);
setOrderData(prev => prev ? ({
...prev,
garments: (prev.garments || []).map(g =>
g.garment_id === garmentId ? { ...g, garment_status: status } : g
),
}) : prev);
 } catch (error) {
 toast.error('Failed to update status');
 throw error; // Re-throw to let the component know
 }
 };

 const handleGarmentUpdate = async (garmentId, updates) => {
 try {
 const garment = orderData.garments.find(g => g.garment_id === garmentId);
 if (garment) {
 await updateGarment(garmentId, {
 order_id: orderId,
 garment_type: garment.garment_type,
 production_factory_id: updates.production_factory_id !== undefined ? updates.production_factory_id : garment.production_factory_id,
 priority: updates.priority !== undefined ? updates.priority : garment.priority,
 pattern_no: updates.pattern_no !== undefined ? updates.pattern_no : garment.pattern_no,
 pattern_status: updates.pattern_status !== undefined ? updates.pattern_status : garment.pattern_status,
 planned_dispatch_date: updates.planned_dispatch_date !== undefined ? updates.planned_dispatch_date : garment.planned_dispatch_date,
client_final_date: updates.client_final_date !== undefined ? updates.client_final_date : garment.client_final_date,
internal_delivery_date: updates.internal_delivery_date !== undefined ? updates.internal_delivery_date : garment.internal_delivery_date,
 order_sheet_status: garment.order_sheet_status || 'not_ready',
 notes: updates.notes !== undefined ? updates.notes : garment.notes,
 garment_status: updates.garment_status !== undefined ? updates.garment_status : garment.garment_status,
 });
setOrderData(prev => prev ? ({
...prev,
garments: (prev.garments || []).map(g =>
g.garment_id === garmentId ? { ...g, ...updates } : g
),
}) : prev);
 }
 } catch (error) {
 toast.error('Failed to update garment');
 throw error; // Re-throw to let the component know
 }
 };

 const handleGarmentDelete = async (garmentId) => {
 // Open confirmation dialog instead of window.confirm
 const garment = orderData.garments?.find(g => g.garment_id === garmentId);
 setGarmentToDelete(garment);
 setDeleteGarmentOption('garment_only');
 setDeleteGarmentDialogOpen(true);
 };

 const handleConfirmDeleteGarment = async () => {
 if (!garmentToDelete) return;
 
 try {
 if (deleteGarmentOption === 'with_line_item') {
 // Delete garment and associated line item
 await deleteGarment(garmentToDelete.garment_id);
 
 // Remove line item from local state
 if (garmentToDelete.line_item_id) {
 const updatedLineItems = lineItems.filter(li => li.line_item_id !== garmentToDelete.line_item_id);
 setLineItems(updatedLineItems);
 
 // Save to backend
 const itemsWithRefs = generateLineItemRefs(updatedLineItems, products, orderData.order_number);
 await updateOrder(orderId, {
 customer_id: formData.customer_id || orderData.customer_id,
order_date: formData.order_date || null,
status: formData.status,
 client_final_date: formData.client_final_date || null,
 internal_delivery_date: formData.internal_delivery_date || null,
 test_fit_date: formData.test_fit_date || null,
 store: formData.store === '__none__' ? null : formData.store,
 notes: formData.notes,
 gst_percentage: parseFloat(formData.gst_percentage) || 0,
 discount_type: formData.discount_type === '__none__' ? null : formData.discount_type,
 discount_value: parseFloat(formData.discount_value) || 0,
 line_items: itemsWithRefs,
 });
 }
 
 toast.success('Garment and line item deleted');
 } else {
 // Delete garment only (leaves line item)
 await deleteGarment(garmentToDelete.garment_id);
 toast.success('Garment deleted (line item preserved)');
 }
 
 setDeleteGarmentDialogOpen(false);
 setGarmentToDelete(null);
setOrderData(prev => prev ? ({
...prev,
garments: (prev.garments || []).filter(g => g.garment_id !== garmentToDelete.garment_id),
fabric_orders: (prev.fabric_orders || []).filter(fo => fo.garment_id !== garmentToDelete.garment_id),
}) : prev);
await fetchOrderData({ silent: true });
 } catch (error) {
 toast.error('Failed to delete garment');
 }
 };

 const handleOpenGarmentImages = (garment) => {
 setSelectedGarment(garment);
 setGarmentImagesOpen(true);
 };

 // Handler to manually add a garment (e.g., for test jackets)
 const handleAddManualGarment = async () => {
 const garmentType = window.prompt('Enter garment type (e.g., "Test Jacket", "Test Trouser"):');
 if (!garmentType || !garmentType.trim()) return;
 
 try {
 await createGarment({
 order_id: orderId,
 line_item_id: null, // Manual garment, not linked to a line item
 garment_type: garmentType.trim(),
 production_factory_id: null,
 garment_status: 'not_started',
 notes: 'Manually added garment',
 });
toast.success(`${garmentType} added`);
fetchOrderData();
 } catch (error) {
 toast.error('Failed to add garment');
 }
 };

 // Handler for Add Garment button
 const handleOpenAddGarmentDialog = () => {
 setRemakeGarmentData(null);
 setAddGarmentDialogOpen(true);
 };

 // Handler for Remake Garment from 3-dot menu
 const handleRemakeGarment = (garment) => {
 setRemakeGarmentData(garment);
 setAddGarmentDialogOpen(true);
 };

 // Handler when garment is created
 const handleGarmentCreated = (newGarment) => {
 fetchOrderData(); // Refresh order data
 };

 // Fabric Order Handlers
 const handleAddFabricOrder = async () => {
 try {
 await createFabricOrder({
 order_id: orderId,
 garment_id: fabricOrderForm.garment_id === '__none__' ? null : fabricOrderForm.garment_id,
 supplier_id: fabricOrderForm.supplier_id === '__none__' ? null : fabricOrderForm.supplier_id,
 fabric_sku: fabricOrderForm.fabric_sku || null,
 fabric_type: fabricOrderForm.fabric_type || 'main_fabric',
 quantity: null, // Quantity will be set later using calculator
 priority: 'medium',
 fabric_order_status: 'not_ordered',
 notes: fabricOrderForm.notes || null,
 });
 toast.success('Fabric order added');
 setFabricOrderDialogOpen(false);
 setFabricOrderForm({ garment_id: '__none__', supplier_id: '__none__', fabric_sku: '', fabric_type: 'main_fabric', quantity: '', notes: '' });
 fetchOrderData();
 } catch (error) {
 toast.error('Failed to add fabric order');
 }
 };

 const handleDeleteFabricOrder = async (fabricId) => {
 try {
 await deleteFabricOrder(fabricId);
 toast.success('Fabric order deleted');
setOrderData(prev => prev ? ({
...prev,
fabric_orders: (prev.fabric_orders || []).filter(fo => fo.fabric_id !== fabricId),
}) : prev);
 } catch (error) {
 toast.error('Failed to delete');
 }
 };

 const handleFabricOrderStatusChange = async (fabricId, status) => {
 try {
 await updateFabricOrderStatus(fabricId, status);
setOrderData(prev => prev ? ({
...prev,
fabric_orders: (prev.fabric_orders || []).map(fo =>
fo.fabric_id === fabricId ? { ...fo, fabric_order_status: status } : fo
),
}) : prev);
 } catch (error) {
 toast.error('Failed to update');
 throw error; // Re-throw to let the component know
 }
 };

 const handleUpdateFabricOrder = async (fabricId, updates) => {
 try {
 await inlineUpdateFabricOrder(fabricId, updates);
setOrderData(prev => prev ? ({
...prev,
fabric_orders: (prev.fabric_orders || []).map(fo =>
fo.fabric_id === fabricId ? { ...fo, ...updates } : fo
),
}) : prev);
 } catch (error) {
 toast.error('Failed to update fabric order');
 throw error; // Re-throw to let the component know
 }
 };

 // Field Deletion Handler
 const handleDeleteField = (categoryId, optionId, option) => {
  setFieldToDelete({ categoryId, optionId, option });
  setDeleteFieldDialogOpen(true);
 };

 const confirmDeleteField = () => {
  if (!fieldToDelete) return;
  
  const { categoryId, optionId } = fieldToDelete;
  
  // Remove the field from productCustomizations
  setProductCustomizations(prev => ({
   ...prev,
   categories: prev.categories.map(cat => {
    if (cat.category_id === categoryId) {
     return {
      ...cat,
      options: cat.options.filter(opt => opt.option_id !== optionId)
     };
    }
    return cat;
   })
  }));
  
  // Remove the value from customizationValues
  const newValues = { ...customizationValues };
  delete newValues[optionId];
  setCustomizationValues(newValues);
  
  toast.success('Field removed from this line item');
  setDeleteFieldDialogOpen(false);
  setFieldToDelete(null);
 };

 // Custom Field Addition Handler
 const handleOpenAddCustomField = (categoryId) => {
  setCustomFieldCategoryId(categoryId);
  setCustomFieldForm({ field_name: '', field_value: '' });
  setAddCustomFieldDialogOpen(true);
 };

 const handleConfirmAddCustomField = () => {
  if (!customFieldCategoryId || !customFieldForm.field_name.trim()) {
   toast.error('Please enter a field name');
   return;
  }
  
  // Generate unique ID for custom field
  const customFieldId = `custom-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`;
  
  // Create new custom field option
  const newOption = {
   option_id: customFieldId,
   name: customFieldForm.field_name.trim(),
   option_type: 'text',
   is_required: false,
   price_impact: 0,
   placeholder: '',
   is_custom: true, // Mark as custom field
   display_order: 999 // Put at end
  };
  
  // Add field to productCustomizations
  setProductCustomizations(prev => ({
   ...prev,
   categories: prev.categories.map(cat => {
    if (cat.category_id === customFieldCategoryId) {
     return {
      ...cat,
      options: [...(cat.options || []), newOption]
     };
    }
    return cat;
   })
  }));
  
  // Set the initial value
  setCustomizationValues({
   ...customizationValues,
   [customFieldId]: customFieldForm.field_value.trim()
  });
  
  toast.success('Custom field added');
  setAddCustomFieldDialogOpen(false);
  setCustomFieldCategoryId(null);
  setCustomFieldForm({ field_name: '', field_value: '' });
 };

 // Payment Handlers
 const handleOpenPaymentDialog = (payment = null) => {
 if (payment) {
 setEditingPayment(payment);
 setPaymentForm({
 amount: payment.amount?.toString() || '',
 payment_method: payment.payment_method || '__none__',
 payment_date: payment.payment_date?.split('T')[0] || '',
 notes: payment.notes || '',
 });
 } else {
 setEditingPayment(null);
 setPaymentForm({ amount: '', payment_method: '__none__', payment_date: new Date().toISOString().split('T')[0], notes: '' });
 }
 setPaymentDialogOpen(true);
 };

 const handleSavePayment = async () => {
 try {
 const data = {
 order_id: orderId,
 amount: parseFloat(paymentForm.amount),
 payment_method: paymentForm.payment_method === '__none__' ? 'Cash' : paymentForm.payment_method,
 payment_date: paymentForm.payment_date || new Date().toISOString(),
 notes: paymentForm.notes,
 };
 
 if (editingPayment) {
 await updatePayment(editingPayment.payment_id, data);
 toast.success('Payment updated');
 } else {
 await createPayment(data);
 toast.success('Payment added');
 }
 setPaymentDialogOpen(false);
 fetchOrderData();
 } catch (error) {
 toast.error('Failed to save payment');
 }
 };

 const handleDeletePayment = async (paymentId) => {
 try {
 await deletePayment(paymentId);
 toast.success('Payment deleted');
 fetchOrderData();
 } catch (error) {
 toast.error('Failed to delete');
 }
 };

 // Interaction Handlers
 const handleOpenInteractionDialog = (interaction = null) => {
 if (interaction) {
 setEditingInteraction(interaction);
 setInteractionForm({
 interaction_type: interaction.interaction_type || '__none__',
 interaction_date: interaction.interaction_date?.split('T')[0] || '',
 notes: interaction.notes || '',
 conducted_by: interaction.conducted_by || '',
 });
 } else {
 setEditingInteraction(null);
 setInteractionForm({ interaction_type: '__none__', interaction_date: new Date().toISOString().split('T')[0], notes: '', conducted_by: '' });
 }
 setInteractionDialogOpen(true);
 };

 const handleSaveInteraction = async () => {
 try {
 const data = {
 order_id: orderId,
 interaction_type: interactionForm.interaction_type === '__none__' ? 'Other' : interactionForm.interaction_type,
 interaction_date: interactionForm.interaction_date || new Date().toISOString(),
 notes: interactionForm.notes,
 conducted_by: interactionForm.conducted_by,
 };
 
 if (editingInteraction) {
 await updateOrderInteraction(editingInteraction.interaction_id, data);
 toast.success('Interaction updated');
 } else {
 await createOrderInteraction(data);
 toast.success('Interaction logged');
 }
 setInteractionDialogOpen(false);
 fetchOrderData();
 } catch (error) {
 toast.error('Failed to save interaction');
 }
 };

 const handleDeleteInteraction = async (interactionId) => {
 try {
 await deleteOrderInteraction(interactionId);
 toast.success('Interaction deleted');
 fetchOrderData();
 } catch (error) {
 toast.error('Failed to delete');
 }
 };

 // Alteration Handlers
 const handleOpenAlterationDialog = (alteration = null) => {
 if ((orderData?.garments || []).length > 0 && factories.length === 0) {
  fetchFactories();
 }
 if (alteration) {
 setEditingAlteration(alteration);
 setAlterationForm({
 garment_id: alteration.garment_id || '__none__',
 description: alteration.description || '',
 cost: alteration.cost?.toString() || '',
 tailor_name: alteration.tailor_name || '',
 alteration_date: alteration.alteration_date?.split('T')[0] || '',
 status: alteration.status || 'pending',
 });
 } else {
 setEditingAlteration(null);
 setAlterationForm({ garment_id: '__none__', description: '', cost: '', tailor_name: '', alteration_date: '', status: 'pending' });
 }
 setAlterationDialogOpen(true);
 };

 const handleSaveAlteration = async () => {
 try {
 const data = {
 order_id: orderId,
 garment_id: alterationForm.garment_id === '__none__' ? null : alterationForm.garment_id,
 description: alterationForm.description,
 cost: parseFloat(alterationForm.cost) || 0,
 tailor_name: alterationForm.tailor_name,
 alteration_date: alterationForm.alteration_date || null,
 };
 
 if (editingAlteration) {
 await updateAlteration(editingAlteration.alteration_id, data);
 if (alterationForm.status !== editingAlteration.status) {
 await updateAlterationStatus(editingAlteration.alteration_id, alterationForm.status);
 }
 toast.success('Alteration updated');
 } else {
 await createAlteration(data);
 toast.success('Alteration added');
 }
 setAlterationDialogOpen(false);
 fetchOrderData();
 } catch (error) {
 toast.error('Failed to save alteration');
 }
 };

 const handleDeleteAlteration = async (alterationId) => {
 try {
 await deleteAlteration(alterationId);
 toast.success('Alteration deleted');
 fetchOrderData();
 } catch (error) {
 toast.error('Failed to delete');
 }
 };

 // Task Handlers
 const handleOpenTaskDialog = (task = null) => {
 fetchUsers();
 if (task) {
 setEditingTask(task);
 // Handle both old string format and new array format
 let assignedTo = [];
 if (task.assigned_to) {
 if (Array.isArray(task.assigned_to)) {
 assignedTo = task.assigned_to;
 } else if (typeof task.assigned_to === 'string') {
 assignedTo = task.assigned_to.split(',').map(u => u.trim()).filter(Boolean);
 }
 }
 
 setTaskForm({
 title: task.title || '',
 description: task.description || '',
 assigned_to: assignedTo,
 due_date: task.due_date?.split('T')[0] || '',
 priority: task.priority || 'medium',
 status: task.status || 'pending',
 });
 } else {
 setEditingTask(null);
 setTaskForm({ title: '', description: '', assigned_to: [], due_date: '', priority: 'medium', status: 'pending' });
 }
 setTaskDialogOpen(true);
 };

 const handleSaveTask = async () => {
 try {
 const data = {
 title: taskForm.title,
 description: taskForm.description,
 assigned_to: taskForm.assigned_to,
 due_date: taskForm.due_date || null,
 priority: taskForm.priority,
 associated_entity_type: 'order',
 associated_entity_id: orderId,
 };
 
 if (editingTask) {
 await updateTask(editingTask.task_id, data);
 if (taskForm.status !== editingTask.status) {
 await updateTaskStatus(editingTask.task_id, taskForm.status);
 }
 toast.success('Task updated');
 } else {
 await createTask(data);
 toast.success('Task created');
 }
 setTaskDialogOpen(false);
 fetchOrderData();
 } catch (error) {
 toast.error('Failed to save task');
 }
 };

 const handleDeleteTask = async (taskId) => {
 try {
 await deleteTask(taskId);
 toast.success('Task deleted');
 fetchOrderData();
 } catch (error) {
 toast.error('Failed to delete');
 }
 };

 // Invoice and Email handlers
 const handleDownloadInvoice = async () => {
 try {
 const API_URL = getApiBaseUrl();
 const token = localStorage.getItem('token');
 
 // Call backend API to generate and download PDF
 const response = await fetch(`${API_URL}/api/orders/${orderId}/invoice/pdf`, {
 method: 'GET',
 headers: {
 'Authorization': `Bearer ${token}`
 }
 });
 
 if (!response.ok) {
 throw new Error('Failed to generate PDF');
 }
 
 // Get the PDF blob
 const blob = await response.blob();
 
 // Create download link
 const url = window.URL.createObjectURL(blob);
 const a = document.createElement('a');
 a.href = url;
 a.download = `Invoice-${orderData.order_number || 'ORDER'}.pdf`;
 document.body.appendChild(a);
 a.click();
 
 // Cleanup
 window.URL.revokeObjectURL(url);
 document.body.removeChild(a);
 
 toast.success('Invoice downloaded successfully');
 } catch (error) {
 console.error('Failed to generate invoice:', error);
 toast.error('Failed to generate invoice. Please try again.');
 }
 };

 const handleSendEmail = async () => {
 if (!orderData?.customer_email) {
 toast.error('Customer email not found. Please add customer email to send invoice.');
 return;
 }

 const confirmed = window.confirm(
 `Send invoice to ${orderData.customer_email}?\n\nThis will email the order invoice with PDF attachment to the customer.`
 );
 
 if (!confirmed) return;

 setSaving(true);
 try {
 await sendOrderInvoiceEmail(orderId);
 toast.success(`Invoice email sent successfully to ${orderData.customer_email}`);
 } catch (error) {
 console.error('Failed to send email:', error);
 const errorMessage = error.response?.data?.detail || 'Failed to send invoice email. Please try again.';
 toast.error(errorMessage);
 } finally {
 setSaving(false);
 }
 };

 // ============== RENDER ==============
 if (loading) {
 return (
 <div className="flex items-center justify-center min-h-[400px]">
 <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
 </div>
 );
 }

 if (!orderData) {
 return (
 <div className="text-center py-12">
 <AlertCircle className="w-12 h-12 mx-auto mb-4 text-slate-400" />
 <p className="text-slate-500">Order not found</p>
 <Button onClick={() => navigate('/orders')} className="mt-4">Back to Orders</Button>
 </div>
 );
 }

 return (
 <div className="min-h-screen bg-gray-50 p-1 sm:p-2">
 <div className="max-w-[1880px] mx-auto">
 {/* New Modern Header */}
 <OrderHeader
 orderData={orderData}
 currentStatus={formData.status}
 hasUnsavedChanges={hasUnsavedChanges}
 saving={saving}
 onSave={handleStatusSave}
 onGeneratePDF={handleDownloadInvoice}
 onSendEmail={handleSendEmail}
 onStatusChange={(value) => {
 setFormData(prev => ({ ...prev, status: value }));
 }}
 />

 {/* Two-Panel Workspace */}
 <div
 className="grid grid-cols-1 xl:grid-cols-[330px_minmax(0,1fr)] gap-3 mb-4"
 data-testid="edit-order-two-panel-layout"
 >
 <aside className="space-y-3 xl:sticky xl:top-2 self-start" data-testid="edit-order-left-summary-panel">

 <CustomerInfoCard
 orderData={orderData}
 formData={formData}
 stores={stores}
 currentStore={formData.store}
 reassigningCustomer={reassigningCustomer}
 onReassignCustomer={handleReassignCustomer}
 onStoreChange={(value) => {
 setFormData(prev => ({ ...prev, store: value }));
 setHasUnsavedChanges(true);
 }}
 onFormChange={(field, value) => {
if (['order_date', 'test_fit_date', 'internal_delivery_date', 'client_final_date'].includes(field)) {
handleDateFieldChangeRequest(field, value);
return;
}
setFormData({ ...formData, [field]: value });
setHasUnsavedChanges(true);
 }}
 editable={true}
 />

 <FinancialSummaryCard
 calculatedTotals={calculatedTotals}
 orderData={orderData}
 formData={formData}
 onFormChange={(field, value) => {
 setFormData({ ...formData, [field]: value });
 setHasUnsavedChanges(true);
 }}
 editable={true}
 />
 </aside>

 <section className="bg-white rounded-lg shadow-sm p-2 min-w-0" data-testid="edit-order-right-tabs-panel">
 <Tabs value={activeTab} onValueChange={setActiveTab}>
 <TabsList className="bg-slate-100 p-1 rounded-lg border border-slate-200 w-full flex flex-nowrap items-center justify-start gap-0.5 overflow-hidden h-auto" data-testid="edit-order-main-tabs-list">
 <TabsTrigger value="line_items" className={TAB_TRIGGER_CLASS} data-testid="edit-order-tab-line-items">
 <FileText className="w-3.5 h-3.5 mr-1 shrink-0" />
 <span className="truncate" title="Line Items">Items ({clientLineItems.length})</span>
 </TabsTrigger>
 <TabsTrigger value="garments" className={TAB_TRIGGER_CLASS} data-testid="edit-order-tab-garments">
 <Shirt className="w-3.5 h-3.5 mr-1 shrink-0" />
 <span className="truncate">Garments ({clientGarments.length})</span>
 </TabsTrigger>
 <TabsTrigger value="fabric_orders" className={TAB_TRIGGER_CLASS} data-testid="edit-order-tab-fabric-orders">
 <Layers className="w-3.5 h-3.5 mr-1 shrink-0" />
 <span className="truncate" title="Fabric Orders">Fabric ({orderData.fabric_orders?.length || 0})</span>
 </TabsTrigger>
 <TabsTrigger value="payments" className={TAB_TRIGGER_CLASS} data-testid="edit-order-tab-payments">
 <CreditCard className="w-3.5 h-3.5 mr-1 shrink-0" />
 <span className="truncate">Payments ({orderData.payments_count ?? orderData.payments?.length ?? 0})</span>
 </TabsTrigger>
 <TabsTrigger value="tasks" className={TAB_TRIGGER_CLASS} data-testid="edit-order-tab-tasks">
 <CheckSquare className="w-3.5 h-3.5 mr-1 shrink-0" />
 <span className="truncate">Tasks ({orderData.tasks_count ?? orderData.tasks?.length ?? 0})</span>
 </TabsTrigger>
 <TabsTrigger value="interactions" className={TAB_TRIGGER_CLASS} data-testid="edit-order-tab-interactions">
 <MessageSquare className="w-3.5 h-3.5 mr-1 shrink-0" />
 <span className="truncate" title="Interactions">Interactions ({orderData.interactions_count ?? orderData.interactions?.length ?? 0})</span>
 </TabsTrigger>
 <TabsTrigger value="alterations" className={TAB_TRIGGER_CLASS} data-testid="edit-order-tab-alterations">
 <Scissors className="w-3.5 h-3.5 mr-1 shrink-0" />
 <span className="truncate" title="Alterations">Alterations ({orderData.alterations_count ?? orderData.alterations?.length ?? 0})</span>
 </TabsTrigger>
 <TabsTrigger value="emails" className={TAB_TRIGGER_CLASS} data-testid="edit-order-tab-emails">
 <Mail className="w-3.5 h-3.5 mr-1 shrink-0" />
 <span className="truncate">Emails</span>
 </TabsTrigger>
 <TabsTrigger value="history" className={TAB_TRIGGER_CLASS} data-testid="edit-order-tab-history">
 <History className="w-3.5 h-3.5 mr-1 shrink-0" />
 <span className="truncate">History</span>
 </TabsTrigger>
 </TabsList>

 <TabsContent value="line_items" className="space-y-3 mt-2" data-testid="edit-order-tab-content-line-items">
 {!detailsHeavyReady ? (
 <Card className="border border-slate-200 bg-white">
 <CardContent className="p-6 space-y-3" data-testid="edit-order-details-deferred-skeleton">
 <p className="text-sm text-muted-foreground">Preparing detailed sections…</p>
 <Skeleton className="h-8 w-full" />
 <Skeleton className="h-8 w-full" />
 <Skeleton className="h-8 w-2/3" />
 </CardContent>
 </Card>
 ) : (
 <Card className="bg-sky-50/50 border border-sky-100 shadow-sm">
 <CardHeader className="border-b border-sky-100 bg-sky-50/70">
 <div className="flex flex-wrap items-center justify-between gap-3">
 <div>
 <CardTitle className="text-lg text-slate-900">{lineItemViewMode === 'test' ? 'Test Garments' : 'Client Items'}</CardTitle>
 <CardDescription>
 {visibleLineItems.length} shown
 {lineItemViewMode === 'client' ? ` • ${formatCurrency(calculatedTotals.subtotal)} subtotal` : ''}
 {lineItemTypeFilter !== 'all' ? ' • filtered' : ''}
 </CardDescription>
 </div>
 <div className="flex flex-wrap items-center gap-2">
 {selectedLineItems.length > 0 && (
 <Button variant="destructive" size="sm" onClick={handleDeleteSelectedLineItems}>
 <Trash2 className="w-4 h-4 mr-1" />
 Delete ({selectedLineItems.length})
 </Button>
 )}
 <Button
 variant="outline"
 size="sm"
 onClick={() => setCopyDialogOpen(true)}
 className="flex items-center gap-2"
 data-testid="edit-order-copy-from-order-button"
 >
 <ClipboardCopy className="w-4 h-4" />
 Copy from Order
 </Button>
 {lineItemViewMode === 'client' && (
 <>
 <Button variant="outline" size="sm" onClick={handleRecalculate}>
 <RefreshCw className="w-4 h-4 mr-1" />
 Recalculate
 </Button>

 {canSyncDailyStatus && (
 <Button
 variant="outline"
 size="sm"
 onClick={handleSyncToDailyStatus}
 disabled={syncingDailyStatus}
 className="flex items-center gap-2 text-emerald-700 border-emerald-300 hover:bg-emerald-50 hover:text-emerald-800 hover:border-emerald-400"
 data-testid="edit-order-sync-daily-status-button"
 >
 <Link2 className="w-4 h-4" />
 {syncingDailyStatus ? 'Syncing...' : 'Sync to Daily Status'}
 </Button>
 )}

 <Button
 variant="outline"
 size="sm"
 onClick={() => {
 fetchProducts();
 fetchFactories();
 setSelectedProduct(null);
 setProductCustomizations(null);
 setCustomizationValues({});
 setBasePrice(null);
 setLineItemQuantity(1);
 setAddLineItemOpen(true);
 }}
 className="flex items-center gap-2"
 data-testid="edit-order-line-items-add-item-button"
 >
 <Plus className="w-4 h-4" />
 Add Item
 </Button>
 </>
 )}
 {lineItemViewMode === 'test' && (
 <Button
 variant="outline"
 size="sm"
 onClick={handleOpenTestGarmentWizard}
 className="flex items-center gap-2 text-orange-700 border-orange-300 hover:bg-orange-50 hover:text-orange-800 hover:border-orange-400"
 data-testid="edit-order-open-test-garment-wizard-button"
 >
 <Shirt className="w-4 h-4" />
 Add Test Garment
 </Button>
 )}
 </div>
 </div>
 </CardHeader>
 <CardContent className="p-0">
 <OrderItemsViewToolbar
 viewMode={lineItemViewMode}
 onViewModeChange={(mode) => {
 setLineItemViewMode(mode);
 setLineItemTypeFilter('all');
 }}
 clientCount={clientLineItems.length}
 testCount={testLineItems.length}
 typeFilter={lineItemTypeFilter}
 onTypeFilterChange={setLineItemTypeFilter}
 typeCounts={lineItemTypeCounts}
 viewModeTestIdPrefix="edit-order-line-items"
 />
 <div className="flex items-center gap-4 p-4 bg-slate-50 border-b border-slate-200">
 <Checkbox
 checked={visibleLineItems.length > 0 && visibleLineItems.every((item) => selectedLineItems.includes(item.line_item_id))}
 onCheckedChange={handleSelectAllLineItems}
 data-testid="edit-order-line-items-select-all"
 />
 <span className="text-sm text-slate-700 font-medium">Select All</span>
 </div>

 {visibleLineItems.length === 0 ? (
 <div className="p-8 text-center text-slate-500" data-testid="edit-order-line-items-empty-state">
 <Package className="w-12 h-12 mx-auto mb-3 opacity-50" />
 <p>{lineItemViewMode === 'test' ? 'No test garments' : 'No client items'}</p>
 <p className="text-sm">
 {lineItemTypeFilter !== 'all'
  ? 'Try another garment type filter'
  : lineItemViewMode === 'test'
   ? 'Add a test garment to this order'
   : 'Add products to this order'}
 </p>
 </div>
 ) : (
 visibleLineItems.map((item, index) => (
 <LineItemRow
 key={item.line_item_id}
 item={item}
 linkedGarment={primaryLinkedGarmentByLineItemId[item.line_item_id] || null}
 index={index}
 selected={selectedLineItems.includes(item.line_item_id)}
 onSelect={() => {
 if (selectedLineItems.includes(item.line_item_id)) {
 setSelectedLineItems(selectedLineItems.filter((id) => id !== item.line_item_id));
 } else {
 setSelectedLineItems([...selectedLineItems, item.line_item_id]);
 }
 }}
 onEdit={() => handleEditLineItem(item)}
 onDuplicate={() => handleDuplicateLineItem(item)}
 onDelete={() => handleDeleteLineItem(item)}
 onPriceChange={handleLineItemPriceChange}
 onBasePriceChange={handleBasePriceChange}
 orderNumber={orderData.order_number}
 productCategories={[]}
 />
 ))
 )}
 </CardContent>
 </Card>
 )}
 </TabsContent>

 <TabsContent value="garments" className="mt-2" data-testid="edit-order-tab-content-garments">
 {!detailsHeavyReady ? (
 <Card className="border border-slate-200 bg-white">
 <CardContent className="p-6 space-y-3" data-testid="edit-order-garments-deferred-skeleton">
 <p className="text-sm text-muted-foreground">Preparing garments section…</p>
 <Skeleton className="h-8 w-full" />
 <Skeleton className="h-8 w-full" />
 <Skeleton className="h-8 w-2/3" />
 </CardContent>
 </Card>
 ) : (
 <Card className="bg-amber-50/50 border border-amber-100 shadow-sm">
 <CardHeader className="border-b border-amber-100 bg-amber-50/70">
 <CardTitle className="text-lg text-slate-900 flex items-center gap-2">
 <Shirt className="w-5 h-5 text-orange-500" />
 {garmentViewMode === 'test' ? 'Test Garments' : 'Client Garments'} ({visibleGarments.length})
 </CardTitle>
 </CardHeader>
 <CardContent className="p-0">
 <OrderItemsViewToolbar
 viewMode={garmentViewMode}
 onViewModeChange={(mode) => {
 setGarmentViewMode(mode);
 setGarmentTypeFilter('all');
 }}
 clientCount={clientGarments.length}
 testCount={testGarmentRecords.length}
 typeFilter={garmentTypeFilter}
 onTypeFilterChange={setGarmentTypeFilter}
 typeCounts={garmentTypeCounts}
 viewModeTestIdPrefix="edit-order-garments"
 />
 <GarmentSubForm
 garments={visibleGarments}
 fabricOrders={sortedFabricOrders}
 lineItemsWithRefs={sortedLineItemsWithRefs}
 factories={factories}
 stores={stores}
 customerId={orderData.customer_id}
 onStatusChange={handleGarmentStatusChange}
 onUpdate={handleGarmentUpdate}
 onDelete={handleGarmentDelete}
 onOpenImages={handleOpenGarmentImages}
 onAddGarment={garmentViewMode === 'client' ? handleOpenAddGarmentDialog : undefined}
 onRemakeGarment={handleRemakeGarment}
 onRefresh={() => fetchOrderData({ silent: true })}
 products={products}
 />
 </CardContent>
 </Card>
 )}
 </TabsContent>

 <TabsContent value="fabric_orders" className="mt-2" data-testid="edit-order-tab-content-fabric-orders">
 {!detailsHeavyReady ? (
 <Card className="border border-slate-200 bg-white">
 <CardContent className="p-6 space-y-3" data-testid="edit-order-fabric-orders-deferred-skeleton">
 <p className="text-sm text-muted-foreground">Preparing fabric orders section…</p>
 <Skeleton className="h-8 w-full" />
 <Skeleton className="h-8 w-full" />
 <Skeleton className="h-8 w-2/3" />
 </CardContent>
 </Card>
 ) : (
 <Card className="bg-emerald-50/50 border border-emerald-100 shadow-sm">
 <CardHeader className="border-b border-emerald-100 bg-emerald-50/70">
 <CardTitle className="text-lg text-slate-900 flex items-center gap-2">
 <Layers className="w-5 h-5 text-orange-500" />
 Fabric Orders
 </CardTitle>
 </CardHeader>
 <CardContent className="p-0">
 <FabricOrderSubForm
 fabricOrders={sortedFabricOrders}
 garments={sortedGarments}
 lineItemsWithRefs={sortedLineItemsWithRefs}
 suppliers={suppliers}
 orderData={sortedOrderData}
 onAdd={() => {
 fetchSuppliers();
 fetchFactories();
 setFabricOrderDialogOpen(true);
 }}
 onDelete={handleDeleteFabricOrder}
 onStatusChange={handleFabricOrderStatusChange}
 onUpdate={handleUpdateFabricOrder}
 onRefresh={() => fetchOrderData({ silent: true })}
 />
 </CardContent>
 </Card>
 )}
 </TabsContent>

 <TabsContent value="payments" className="mt-2" data-testid="edit-order-tab-content-payments">
 <OrderPaymentsTab
 orderData={sortedOrderData}
 onOpenPaymentDialog={handleOpenPaymentDialog}
 onDeletePayment={handleDeletePayment}
 />
 </TabsContent>

 <TabsContent value="tasks" className="mt-2" data-testid="edit-order-tab-content-tasks">
 <OrderTasksTab
 orderData={sortedOrderData}
 onOpenTaskDialog={handleOpenTaskDialog}
 onDeleteTask={handleDeleteTask}
 />
 </TabsContent>

 <TabsContent value="interactions" className="mt-2" data-testid="edit-order-tab-content-interactions">
 <OrderInteractionsTab
 orderData={sortedOrderData}
 onOpenInteractionDialog={handleOpenInteractionDialog}
 onDeleteInteraction={handleDeleteInteraction}
 />
 </TabsContent>

 <TabsContent value="alterations" className="mt-2" data-testid="edit-order-tab-content-alterations">
 <OrderAlterationsTab
 orderData={orderData}
 onOpenAlterationDialog={handleOpenAlterationDialog}
 onDeleteAlteration={handleDeleteAlteration}
 />
 </TabsContent>

 <TabsContent value="emails" className="mt-2" data-testid="edit-order-tab-content-emails">
 <OrderEmailsTab orderId={orderId} onSendEmail={handleSendEmail} customerEmail={orderData?.customer_email} />
 </TabsContent>

 <TabsContent value="history" className="mt-2" data-testid="edit-order-tab-content-history">
 <OrderHistoryTab orderId={orderId} />
 </TabsContent>
 </Tabs>
 </section>
 </div>

      {/* ============== DIALOGS ============== */}

{shouldMountDialogs && (
<>
{/* Back Confirmation Dialog */}
 <AlertDialog open={showBackConfirm} onOpenChange={setShowBackConfirm}>
 <AlertDialogContent>
 <AlertDialogHeader>
 <AlertDialogTitle>Unsaved Changes</AlertDialogTitle>
 <AlertDialogDescription>
 You have unsaved changes. Are you sure you want to leave? Your changes will be lost.
 </AlertDialogDescription>
 </AlertDialogHeader>
 <AlertDialogFooter>
 <AlertDialogCancel>Stay</AlertDialogCancel>
 <AlertDialogAction onClick={() => navigate('/orders')} className="bg-red-600 hover:bg-red-700">
 Leave
 </AlertDialogAction>
 </AlertDialogFooter>
 </AlertDialogContent>
 </AlertDialog>

 {/* Delete Confirmation Dialog */}
 <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
 <AlertDialogContent>
 <AlertDialogHeader>
 <AlertDialogTitle>
 {itemToDelete?.type === 'bulkLineItems' 
 ? `Delete ${itemToDelete.items?.length} Line Item(s)` 
 : 'Delete Line Item'}
 </AlertDialogTitle>
 <AlertDialogDescription className="space-y-3">
 <p>
 {itemToDelete?.type === 'bulkLineItems'
 ? `Are you sure you want to delete ${itemToDelete.items?.length} selected item(s)?`
 : 'Are you sure you want to delete this item?'}
 </p>
 {itemToDelete?.linkedGarments?.length > 0 && (
 <div className="p-3 bg-orange-50 rounded-md border border-orange-200">
 <p className="font-medium text-orange-800 mb-2">
 {itemToDelete?.type === 'bulkLineItems' 
 ? 'These line items have linked records:'
 : 'This line item has linked records:'}
 </p>
 <ul className="text-sm text-orange-700 space-y-1">
 <li>• {itemToDelete.linkedGarments.length} garment(s)</li>
 {itemToDelete.linkedFabricOrders?.length > 0 && (
 <li>• {itemToDelete.linkedFabricOrders.length} fabric order(s)</li>
 )}
 </ul>
 </div>
 )}
 </AlertDialogDescription>
 </AlertDialogHeader>
 <AlertDialogFooter className="flex-col sm:flex-row gap-2">
 <AlertDialogCancel>Cancel</AlertDialogCancel>
 {itemToDelete?.linkedGarments?.length > 0 ? (
 <>
 <AlertDialogAction 
 onClick={() => handleConfirmDelete(false)} 
 className="bg-slate-600 hover:bg-slate-700"
 >
 {itemToDelete?.type === 'bulkLineItems' ? 'Delete Line Items Only' : 'Delete Line Item Only'}
 </AlertDialogAction>
 <AlertDialogAction 
 onClick={() => handleConfirmDelete(true)} 
 className="bg-red-600 hover:bg-red-700"
 >
 Delete All Linked
 </AlertDialogAction>
 </>
 ) : (
 <AlertDialogAction onClick={() => handleConfirmDelete(false)} className="bg-red-600 hover:bg-red-700">
 Delete
 </AlertDialogAction>
 )}
 </AlertDialogFooter>
 </AlertDialogContent>
 </AlertDialog>

{/* Fabric Sync Decision Dialog (after line-item fabric ID changes) */}
<Dialog open={fabricSyncDialogOpen} onOpenChange={(open) => {
if (!open) {
clearFabricSyncFlow();
return;
}
setFabricSyncDialogOpen(true);
}}>
<DialogContent className="sm:max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="line-item-fabric-sync-dialog">
<DialogHeader>
<DialogTitle>Apply fabric ID changes to linked records?</DialogTitle>
<DialogDescription>
Main/Lining/Contrast/Interlining IDs changed in this line item. Choose update vs create per garment.
</DialogDescription>
</DialogHeader>

<div className="space-y-4 py-2">
<div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
<p className="text-xs font-semibold text-slate-700 mb-2">Detected Fabric ID Changes</p>
<div className="space-y-1">
{(fabricSyncPreview?.changed_fabrics || []).map((row) => (
<div key={`${row.fabric_type}-${row.new_sku}`} className="text-xs text-slate-700" data-testid={`fabric-sync-change-${row.fabric_type}`}>
<span className="font-medium uppercase">{row.fabric_type.replace('_', ' ')}</span>
<span className="mx-2 text-slate-400">:</span>
<span className="text-slate-500">{row.old_sku || '—'}</span>
<span className="mx-2 text-slate-400">→</span>
<span className="font-medium text-slate-900">{row.new_sku || '—'}</span>
</div>
))}
</div>
</div>

<div className="rounded-lg border border-slate-200 overflow-hidden">
<Table>
<TableHeader>
<TableRow>
<TableHead className="w-[130px]">Garment</TableHead>
<TableHead>Current fabric-order state</TableHead>
<TableHead className="w-[220px]">Decision</TableHead>
</TableRow>
</TableHeader>
<TableBody>
{(fabricSyncPreview?.garments || []).map((garment) => {
const decisionValue = fabricSyncSelections[garment.garment_id] || garment.default_decision || 'update_existing';
const hasEditableRows = garment.has_update_candidates;

return (
<TableRow key={garment.garment_id} data-testid={`fabric-sync-garment-row-${garment.garment_id}`}>
<TableCell>
<div className="font-medium text-slate-800">{garment.garment_no || '—'}</div>
<div className="text-xs text-slate-500">{garment.garment_type || 'garment'}</div>
</TableCell>
<TableCell>
{(garment.existing_fabric_orders || []).length > 0 ? (
<div className="space-y-1">
{garment.existing_fabric_orders.map((row) => (
<div key={`${row.fabric_id}-${row.fabric_type}`} className="text-xs text-slate-700">
<span className="font-medium uppercase">{row.fabric_type.replace('_', ' ')}</span>
<span className="mx-1 text-slate-400">•</span>
<span>{row.fabric_sku || '—'}</span>
<span className="mx-1 text-slate-400">•</span>
<span className={row.can_update ? 'text-emerald-700' : 'text-slate-500'}>
{row.fabric_order_status || '—'}{row.can_update ? ' (updatable)' : ' (locked)'}
</span>
</div>
))}
</div>
) : (
<span className="text-xs text-slate-500">No existing fabric-order rows</span>
)}
{!hasEditableRows && (
<p className="text-[11px] text-amber-700 mt-2">No pending/not_ordered row to update. Use “Create New”.</p>
)}
</TableCell>
<TableCell>
<Select
value={decisionValue}
onValueChange={(value) => handleFabricSyncDecisionChange(garment.garment_id, value)}
>
<SelectTrigger data-testid={`fabric-sync-decision-select-${garment.garment_id}`}>
<SelectValue placeholder="Choose" />
</SelectTrigger>
<SelectContent>
<SelectItem value="update_existing">Update existing (pending/not_ordered only)</SelectItem>
<SelectItem value="create_new">Create new records</SelectItem>
<SelectItem value="skip">Skip this garment</SelectItem>
</SelectContent>
</Select>
</TableCell>
</TableRow>
);
})}
</TableBody>
</Table>
</div>
</div>

<DialogFooter>
<Button variant="outline" onClick={handleCancelFabricSyncSave} data-testid="fabric-sync-cancel-button">Cancel</Button>
<Button onClick={handleConfirmFabricSyncSave} data-testid="fabric-sync-apply-button">
Save Line Item + Apply Decisions
</Button>
</DialogFooter>
</DialogContent>
</Dialog>

{/* Date Propagation Decision Modal */}
<Dialog open={datePropagationModalOpen} onOpenChange={setDatePropagationModalOpen}>
<DialogContent className="sm:max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="edit-order-date-propagation-dialog">
<DialogHeader>
<DialogTitle>
{datePropagationConfig?.type === 'test_fit_date' ? 'Apply Test Fit Date to Related Records?' : 'Apply Date Change to Related Records?'}
</DialogTitle>
<DialogDescription>
{datePropagationConfig?.type === 'test_fit_date'
? 'Select test garments to update Test Fit Date. If you skip, only order-level date will be saved.'
: 'Select garments to apply this date change. If you skip, only order-level date will be saved.'}
</DialogDescription>
</DialogHeader>

<div className="space-y-4">
<div className="rounded-md border p-3">
<div className="flex items-center justify-between mb-2">
<h4 className="text-sm font-semibold">{datePropagationConfig?.type === 'test_fit_date' ? 'Test Garments' : 'Garments'}</h4>
<div className="flex items-center gap-2">
<Checkbox
checked={(relevantGarmentsForDateModal || []).length > 0 && (datePropagationTargets.garmentIds || []).length === (relevantGarmentsForDateModal || []).length}
onCheckedChange={(checked) => toggleDatePropagationSelectAllGarments(!!checked)}
data-testid="edit-order-date-propagation-garments-select-all"
/>
<span className="text-xs text-slate-600">Select all</span>
</div>
</div>
<div className="max-h-48 overflow-y-auto space-y-2">
{(relevantGarmentsForDateModal || []).map((garment) => (
<label key={garment.garment_id} className="flex items-center gap-2 text-sm" data-testid={`edit-order-date-propagation-garment-${garment.garment_id}`}>
<Checkbox
checked={selectedDatePropagationGarmentIds.has(garment.garment_id)}
onCheckedChange={(checked) => toggleDatePropagationGarmentSelection(garment.garment_id, !!checked)}
/>
<span className="font-medium">{garment.garment_no || garment.garment_id}</span>
<Badge variant="outline" className="text-[10px] capitalize">{(garment.garment_status || 'pending').replace(/_/g, ' ')}</Badge>
</label>
))}
{(relevantGarmentsForDateModal || []).length === 0 && (
<p className="text-xs text-slate-500" data-testid="edit-order-date-propagation-no-garments">No matching garments found.</p>
)}
</div>
</div>
</div>

<DialogFooter>
<Button variant="outline" onClick={skipDatePropagationTargets} data-testid="edit-order-date-propagation-skip-button">Skip (Order date only)</Button>
<Button onClick={applyDatePropagationTargets} data-testid="edit-order-date-propagation-apply-button">Apply to Selected</Button>
</DialogFooter>
</DialogContent>
</Dialog>

 <CopyFromOrderDialog
 open={copyDialogOpen}
 onOpenChange={setCopyDialogOpen}
 excludeOrderId={orderId}
 onConfirm={handleCopyFromOrder}
 />

 {/* Add Line Item Product Selection Dialog */}
 <Dialog open={addLineItemOpen} onOpenChange={(open) => {
 setAddLineItemOpen(open);
 if (!open) {
 setSelectedProduct(null);
 setProductCustomizations(null);
 setCustomizationValues({});
 setBasePrice(null);
 setLineItemQuantity(1);
 }
 }}>
 <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="edit-order-add-item-product-dialog">
 <DialogHeader>
 <DialogTitle>Select a Product</DialogTitle>
 <DialogDescription>Choose a product to add to this order</DialogDescription>
 </DialogHeader>
 <div className="grid grid-cols-2 gap-3 py-4" data-testid="edit-order-product-selection-grid">
 {products.filter(p => !p.name.startsWith('Test')).map((product) => (
 <button
 key={product.product_id}
 onClick={() => handleSelectProductForLineItem(product)}
 className="p-4 rounded-lg border border-slate-200 hover:border-primary hover:bg-primary/5 transition-all text-left group"
 data-testid={`edit-order-select-product-${product.product_id}`}
 >
 <div className="flex items-center justify-between">
 <div>
 <p className="font-medium group-hover:text-primary transition-colors">{product.name}</p>
 <p className="text-sm text-muted-foreground">{product.description || 'No description'}</p>
 </div>
 <div className="text-right">
 <p className="font-semibold text-primary">{formatCurrency(product.base_price)}</p>
 </div>
 </div>
 </button>
 ))}
 </div>
 <DialogFooter>
 <Button variant="outline" onClick={() => setAddLineItemOpen(false)} data-testid="edit-order-select-product-cancel-button">Cancel</Button>
 </DialogFooter>
 </DialogContent>
 </Dialog>

 <CustomizationFlowModal
 open={addLineItemCustomizationOpen}
 onOpenChange={setAddLineItemCustomizationOpen}
 title={`Customize ${selectedProduct?.name || ''}`}
 description="Configure options for this product. Enter moves next; Shift+Enter goes previous."
 loading={loading}
 categories={productCustomizations?.categories || []}
 values={customizationValues}
 setValues={setCustomizationValues}
 isOptionVisible={isOptionVisible}
 formatCurrency={formatCurrency}
 requireMandatory={formData.status !== 'enquiry'}
 onSubmit={handleConfirmAddLineItem}
 submitLabel="Add to Order"
 basePriceValue={basePrice ?? selectedProduct?.base_price ?? 0}
 basePriceDefault={selectedProduct?.base_price || 0}
 onBasePriceChange={setBasePrice}
 showQuantity
 quantity={lineItemQuantity}
 onQuantityChange={setLineItemQuantity}
 showAddCustomField
 onAddCustomField={handleOpenAddCustomField}
 onCloseCleanup={() => {
 setSelectedProduct(null);
 setProductCustomizations(null);
 setCustomizationValues({});
 setBasePrice(null);
 setLineItemQuantity(1);
setLineItemFactorySelection('__none__');
 }}
 dataTestIdPrefix="edit-order-add-line-item-customization"
 referencePanel={getFabricReferencePanel(orderData?.line_items)}
 />

{addLineItemCustomizationOpen && (
<div className="fixed top-24 right-8 z-[70] rounded-md border bg-white px-3 py-2 shadow-md" data-testid="edit-order-add-line-item-factory-floating-control">
<Label className="text-[11px] text-slate-600" htmlFor="edit_order_add_line_item_factory_select">Line Item Factory</Label>
<Select
 value={lineItemFactorySelection}
 onValueChange={setLineItemFactorySelection}
>
 <SelectTrigger
 id="edit_order_add_line_item_factory_select"
 className="h-8 min-w-[220px] mt-1 text-xs"
 data-testid="edit-order-add-line-item-factory-select-trigger"
 >
 <SelectValue placeholder="Select factory" />
 </SelectTrigger>
 <SelectContent data-testid="edit-order-add-line-item-factory-select-content">
 <SelectItem value="__none__" data-testid="edit-order-add-line-item-factory-option-none">No factory</SelectItem>
 {factories.map((factory) => (
  <SelectItem
   key={factory.factory_id}
   value={factory.factory_id}
   data-testid={`edit-order-add-line-item-factory-option-${factory.factory_id}`}
  >
   {factory.factory_name} ({factory.factory_code})
  </SelectItem>
 ))}
 </SelectContent>
</Select>
</div>
)}

 {/* Edit Line Item Dialog */}
 <CustomizationFlowModal
 open={editLineItemOpen}
 onOpenChange={setEditLineItemOpen}
 title={`Edit ${selectedProduct?.name || ''}`}
 description="Update customizations for this line item."
 loading={loading}
 categories={productCustomizations?.categories || []}
 values={customizationValues}
 setValues={setCustomizationValues}
 isOptionVisible={isOptionVisible}
 formatCurrency={formatCurrency}
 requireMandatory={formData.status !== 'enquiry'}
 onSubmit={handleConfirmEditLineItem}
 submitLabel="Update"
 basePriceValue={basePrice ?? selectedProduct?.base_price ?? 0}
 basePriceDefault={selectedProduct?.base_price || 0}
 onBasePriceChange={setBasePrice}
 showQuantity
 quantity={lineItemQuantity}
 onQuantityChange={setLineItemQuantity}
 showDelete
 onDeleteField={handleDeleteField}
 showAddCustomField
 onAddCustomField={handleOpenAddCustomField}
 onCloseCleanup={() => {
 setEditLineItemOpen(false);
 setEditingLineItem(null);
 setSelectedProduct(null);
 setProductCustomizations(null);
 setCustomizationValues({});
 setBasePrice(null);
 setLineItemQuantity(1);
setLineItemFactorySelection('__none__');
 }}
 dataTestIdPrefix="edit-order-edit-line-item-customization"
 referencePanel={getFabricReferencePanel(orderData?.line_items, editingLineItem?.line_item_id)}
 />

{editLineItemOpen && (
<div className="fixed top-24 right-8 z-[70] rounded-md border bg-white px-3 py-2 shadow-md" data-testid="edit-order-edit-line-item-factory-floating-control">
<Label className="text-[11px] text-slate-600" htmlFor="edit_order_edit_line_item_factory_select">Line Item Factory</Label>
<Select
 value={lineItemFactorySelection}
 onValueChange={setLineItemFactorySelection}
>
 <SelectTrigger
 id="edit_order_edit_line_item_factory_select"
 className="h-8 min-w-[220px] mt-1 text-xs"
 data-testid="edit-order-edit-line-item-factory-select-trigger"
 >
 <SelectValue placeholder="Select factory" />
 </SelectTrigger>
 <SelectContent data-testid="edit-order-edit-line-item-factory-select-content">
 <SelectItem value="__none__" data-testid="edit-order-edit-line-item-factory-option-none">No factory</SelectItem>
 {factories.map((factory) => (
  <SelectItem
   key={factory.factory_id}
   value={factory.factory_id}
   data-testid={`edit-order-edit-line-item-factory-option-${factory.factory_id}`}
  >
   {factory.factory_name} ({factory.factory_code})
  </SelectItem>
 ))}
 </SelectContent>
</Select>
</div>
)}

 {/* Duplicate Line Item Dialog */}
 <CustomizationFlowModal
 open={duplicateLineItemOpen}
 onOpenChange={setDuplicateLineItemOpen}
 title={`Duplicate ${selectedProduct?.name || ''}`}
 description="Modify customizations for the duplicated item if needed."
 loading={loading}
 categories={productCustomizations?.categories || []}
 values={customizationValues}
 setValues={setCustomizationValues}
 isOptionVisible={isOptionVisible}
 formatCurrency={formatCurrency}
 requireMandatory={formData.status !== 'enquiry'}
 onSubmit={handleConfirmDuplicateLineItem}
 submitLabel={saving ? 'Creating...' : 'Duplicate Item'}
 isSubmitting={saving}
 basePriceValue={basePrice ?? selectedProduct?.base_price ?? 0}
 basePriceDefault={selectedProduct?.base_price || 0}
 onBasePriceChange={setBasePrice}
 showQuantity
 quantity={lineItemQuantity}
 onQuantityChange={setLineItemQuantity}
 onCloseCleanup={() => {
 setDuplicatingLineItem(null);
 setSelectedProduct(null);
 setProductCustomizations(null);
 setCustomizationValues({});
 setBasePrice(null);
 setLineItemQuantity(1);
setLineItemFactorySelection('__none__');
 }}
 dataTestIdPrefix="edit-order-duplicate-line-item-customization"
 referencePanel={getFabricReferencePanel(orderData?.line_items, duplicatingLineItem?.line_item_id)}
 />

{duplicateLineItemOpen && (
<div className="fixed top-24 right-8 z-[70] rounded-md border bg-white px-3 py-2 shadow-md" data-testid="edit-order-duplicate-line-item-factory-floating-control">
<Label className="text-[11px] text-slate-600" htmlFor="edit_order_duplicate_line_item_factory_select">Line Item Factory</Label>
<Select
 value={lineItemFactorySelection}
 onValueChange={setLineItemFactorySelection}
>
 <SelectTrigger
 id="edit_order_duplicate_line_item_factory_select"
 className="h-8 min-w-[220px] mt-1 text-xs"
 data-testid="edit-order-duplicate-line-item-factory-select-trigger"
 >
 <SelectValue placeholder="Select factory" />
 </SelectTrigger>
 <SelectContent data-testid="edit-order-duplicate-line-item-factory-select-content">
 <SelectItem value="__none__" data-testid="edit-order-duplicate-line-item-factory-option-none">No factory</SelectItem>
 {factories.map((factory) => (
  <SelectItem
   key={factory.factory_id}
   value={factory.factory_id}
   data-testid={`edit-order-duplicate-line-item-factory-option-${factory.factory_id}`}
  >
   {factory.factory_name} ({factory.factory_code})
  </SelectItem>
 ))}
 </SelectContent>
</Select>
</div>
)}

 {/* Payment Dialog */}
 <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
 <DialogContent>
 <DialogHeader>
 <DialogTitle>{editingPayment ? 'Edit Payment' : 'Add Payment'}</DialogTitle>
 </DialogHeader>
 <div className="space-y-4 py-4">
 <div className="grid grid-cols-2 gap-4">
 <div className="space-y-1.5">
 <Label>Amount *</Label>
 <Input
 type="number"
 value={paymentForm.amount}
 onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
 className="h-10"
 />
 </div>
 <div className="space-y-1.5">
 <Label>Date</Label>
 <DateField
 value={paymentForm.payment_date}
 onChange={(next) => setPaymentForm({ ...paymentForm, payment_date: next })}
 className="h-10 w-full"
 testId="edit-order-payment-date-input"
 clearTestId="edit-order-payment-date-clear-button"
 />
 </div>
 </div>
 <div className="space-y-1.5">
 <Label>Method</Label>
 <Select value={paymentForm.payment_method} onValueChange={(v) => setPaymentForm({ ...paymentForm, payment_method: v })}>
 <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
 <SelectContent>
 <SelectItem value="__none__">Select...</SelectItem>
 {paymentMethods.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
 </SelectContent>
 </Select>
 </div>
 <div className="space-y-1.5">
 <Label>Notes</Label>
 <Textarea
 value={paymentForm.notes}
 onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
 />
 </div>
 </div>
 <DialogFooter>
 <Button variant="outline" onClick={() => setPaymentDialogOpen(false)}>Cancel</Button>
 <Button onClick={handleSavePayment} disabled={!paymentForm.amount}>
 {editingPayment ? 'Update' : 'Add'} Payment
 </Button>
 </DialogFooter>
 </DialogContent>
 </Dialog>

 {/* Interaction Dialog */}
 <Dialog open={interactionDialogOpen} onOpenChange={setInteractionDialogOpen}>
 <DialogContent>
 <DialogHeader>
 <DialogTitle>{editingInteraction ? 'Edit Interaction' : 'Log Interaction'}</DialogTitle>
 </DialogHeader>
 <div className="space-y-4 py-4">
 <div className="grid grid-cols-2 gap-4">
 <div className="space-y-1.5">
 <Label>Type *</Label>
 <Select value={interactionForm.interaction_type} onValueChange={(v) => setInteractionForm({ ...interactionForm, interaction_type: v })}>
 <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
 <SelectContent>
 <SelectItem value="__none__">Select...</SelectItem>
 {interactionTypes.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
 </SelectContent>
 </Select>
 </div>
 <div className="space-y-1.5">
 <Label>Date</Label>
 <DateField
 value={interactionForm.interaction_date}
 onChange={(next) => setInteractionForm({ ...interactionForm, interaction_date: next })}
 className="h-10 w-full"
 testId="edit-order-interaction-date-input"
 clearTestId="edit-order-interaction-date-clear-button"
 />
 </div>
 </div>
 <div className="space-y-1.5">
 <Label>Conducted By</Label>
 <Input
 value={interactionForm.conducted_by}
 onChange={(e) => setInteractionForm({ ...interactionForm, conducted_by: e.target.value })}
 className="h-10"
 />
 </div>
 <div className="space-y-1.5">
 <Label>Notes</Label>
 <Textarea
 value={interactionForm.notes}
 onChange={(e) => setInteractionForm({ ...interactionForm, notes: e.target.value })}
 />
 </div>
 </div>
 <DialogFooter>
 <Button variant="outline" onClick={() => setInteractionDialogOpen(false)}>Cancel</Button>
 <Button onClick={handleSaveInteraction}>
 {editingInteraction ? 'Update' : 'Log'} Interaction
 </Button>
 </DialogFooter>
 </DialogContent>
 </Dialog>

 {/* Alteration Dialog */}
 <Dialog open={alterationDialogOpen} onOpenChange={setAlterationDialogOpen}>
 <DialogContent>
 <DialogHeader>
 <DialogTitle>{editingAlteration ? 'Edit Alteration' : 'Add Alteration'}</DialogTitle>
 </DialogHeader>
 <div className="space-y-4 py-4">
 <div className="grid grid-cols-2 gap-4">
 <div className="space-y-1.5">
 <Label>Garment</Label>
 <Select value={alterationForm.garment_id} onValueChange={(v) => setAlterationForm({ ...alterationForm, garment_id: v })}>
 <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
 <SelectContent>
 <SelectItem value="__none__">Select garment...</SelectItem>
 {orderData.garments?.map(g => (
 <SelectItem key={g.garment_id} value={g.garment_id}>
 {g.garment_no} - {g.garment_type}
 </SelectItem>
 ))}
 </SelectContent>
 </Select>
 </div>
 <div className="space-y-1.5">
 <Label>Date</Label>
 <DateField
 value={alterationForm.alteration_date}
 onChange={(next) => setAlterationForm({ ...alterationForm, alteration_date: next })}
 className="h-10 w-full"
 testId="edit-order-alteration-date-input"
 clearTestId="edit-order-alteration-date-clear-button"
 />
 </div>
 </div>
 <div className="space-y-1.5">
 <Label>Description *</Label>
 <Textarea
 value={alterationForm.description}
 onChange={(e) => setAlterationForm({ ...alterationForm, description: e.target.value })}
 />
 </div>
 <div className="grid grid-cols-3 gap-4">
 <div className="space-y-1.5">
 <Label>Tailor</Label>
 <Input
 value={alterationForm.tailor_name}
 onChange={(e) => setAlterationForm({ ...alterationForm, tailor_name: e.target.value })}
 className="h-10"
 />
 </div>
 <div className="space-y-1.5">
 <Label>Cost</Label>
 <Input
 type="number"
 value={alterationForm.cost}
 onChange={(e) => setAlterationForm({ ...alterationForm, cost: e.target.value })}
 className="h-10"
 />
 </div>
 <div className="space-y-1.5">
 <Label>Status</Label>
 <Select value={alterationForm.status} onValueChange={(v) => setAlterationForm({ ...alterationForm, status: v })}>
 <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
 <SelectContent>
 {alterationStatuses.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
 </SelectContent>
 </Select>
 </div>
 </div>
 </div>
 <DialogFooter>
 <Button variant="outline" onClick={() => setAlterationDialogOpen(false)}>Cancel</Button>
 <Button onClick={handleSaveAlteration} disabled={!alterationForm.description}>
 {editingAlteration ? 'Update' : 'Add'} Alteration
 </Button>
 </DialogFooter>
 </DialogContent>
 </Dialog>

 {/* Task Dialog */}
 <Dialog open={taskDialogOpen} onOpenChange={setTaskDialogOpen}>
 <DialogContent>
 <DialogHeader>
 <DialogTitle>{editingTask ? 'Edit Task' : 'Add Task'}</DialogTitle>
 </DialogHeader>
 <div className="space-y-4 py-4">
 <div className="space-y-1.5">
 <Label>Title *</Label>
 <Input
 value={taskForm.title}
 onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })}
 className="h-10"
 />
 </div>
 <div className="space-y-1.5">
 <Label>Description</Label>
 <Textarea
 value={taskForm.description}
 onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })}
 />
 </div>
 <div className="grid grid-cols-2 gap-4">
 <div className="space-y-1.5">
 <Label>Assigned To</Label>
 <Popover>
 <PopoverTrigger asChild>
 <Button
 variant="outline"
 role="combobox"
 className="w-full h-10 justify-between font-normal"
 >
 {taskForm.assigned_to.length > 0 ? (
 <span className="truncate">
 {taskForm.assigned_to.length} user(s) selected
 </span>
 ) : (
 <span className="text-muted-foreground">Select users...</span>
 )}
 <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
 </Button>
 </PopoverTrigger>
 <PopoverContent className="w-[280px] p-0" align="start">
 <Command>
 <CommandInput placeholder="Search users..." />
 <CommandEmpty>No user found.</CommandEmpty>
 <CommandList>
 <CommandGroup className="max-h-64 overflow-auto">
 {users.map((user) => (
 <CommandItem
 key={user.id || user.email}
 onSelect={() => {
 const userName = user.name || user.email;
 const newAssigned = taskForm.assigned_to.includes(userName)
 ? taskForm.assigned_to.filter(u => u !== userName)
 : [...taskForm.assigned_to, userName];
 setTaskForm({ ...taskForm, assigned_to: newAssigned });
 }}
 >
 <Checkbox
 checked={taskForm.assigned_to.includes(user.name || user.email)}
 className="mr-2"
 />
 {user.name || user.email}
 </CommandItem>
 ))}
 </CommandGroup>
 </CommandList>
 </Command>
 </PopoverContent>
 </Popover>
 {taskForm.assigned_to.length > 0 && (
 <div className="flex flex-wrap gap-1 mt-1">
 {taskForm.assigned_to.map(userName => (
 <Badge key={userName} variant="secondary" className="text-xs">
 {userName}
 <button
 type="button"
 onClick={() => setTaskForm({ ...taskForm, assigned_to: taskForm.assigned_to.filter(u => u !== userName) })}
 className="ml-1 hover:text-red-600"
 >
 ×
 </button>
 </Badge>
 ))}
 </div>
 )}
 </div>
 <div className="space-y-1.5">
 <Label>Due Date</Label>
 <DateField
 value={taskForm.due_date}
 onChange={(next) => setTaskForm({ ...taskForm, due_date: next })}
 className="h-10 w-full"
 testId="edit-order-task-due-date-input"
 clearTestId="edit-order-task-due-date-clear-button"
 />
 </div>
 </div>
 <div className="grid grid-cols-2 gap-4">
 <div className="space-y-1.5">
 <Label>Priority</Label>
 <Select value={taskForm.priority} onValueChange={(v) => setTaskForm({ ...taskForm, priority: v })}>
 <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
 <SelectContent>
 {priorityOptions.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
 </SelectContent>
 </Select>
 </div>
 {editingTask && (
 <div className="space-y-1.5">
 <Label>Status</Label>
 <Select value={taskForm.status} onValueChange={(v) => setTaskForm({ ...taskForm, status: v })}>
 <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
 <SelectContent>
 {taskStatuses.map(s => <SelectItem key={s} value={s}>{s.replace('_', ' ')}</SelectItem>)}
 </SelectContent>
 </Select>
 </div>
 )}
 </div>
 </div>
 <DialogFooter>
 <Button variant="outline" onClick={() => setTaskDialogOpen(false)}>Cancel</Button>
 <Button onClick={handleSaveTask} disabled={!taskForm.title}>
 {editingTask ? 'Update' : 'Add'} Task
 </Button>
 </DialogFooter>
 </DialogContent>
 </Dialog>

 {/* Fabric Order Dialog */}
 <Dialog open={fabricOrderDialogOpen} onOpenChange={setFabricOrderDialogOpen}>
 <DialogContent>
 <DialogHeader>
 <DialogTitle>Add Fabric Order</DialogTitle>
 </DialogHeader>
 <div className="space-y-4 py-4">
 <div className="grid grid-cols-2 gap-4">
 <div className="space-y-1.5">
 <Label>Garment</Label>
 <Select value={fabricOrderForm.garment_id} onValueChange={(v) => setFabricOrderForm({ ...fabricOrderForm, garment_id: v })}>
 <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
 <SelectContent>
 <SelectItem value="__none__">Select garment...</SelectItem>
 {orderData.garments?.filter(g => !g.is_test_garment).map(g => (
 <SelectItem key={g.garment_id} value={g.garment_id}>
 {g.garment_no} - {g.garment_type}
 </SelectItem>
 ))}
 </SelectContent>
 </Select>
 </div>
 <div className="space-y-1.5">
 <Label>Fabric Type</Label>
 <Select value={fabricOrderForm.fabric_type} onValueChange={(v) => setFabricOrderForm({ ...fabricOrderForm, fabric_type: v })}>
 <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
 <SelectContent>
 {fabricTypes.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
 </SelectContent>
 </Select>
 </div>
 </div>
 <div className="grid grid-cols-2 gap-4">
 <div className="space-y-1.5">
 <Label>Supplier</Label>
 <Select value={fabricOrderForm.supplier_id} onValueChange={(v) => setFabricOrderForm({ ...fabricOrderForm, supplier_id: v })}>
 <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
 <SelectContent>
 <SelectItem value="__none__">Select supplier...</SelectItem>
 {suppliers.map(s => <SelectItem key={s.supplier_id} value={s.supplier_id}>{s.supplier_name}</SelectItem>)}
 </SelectContent>
 </Select>
 </div>
 <div className="space-y-1.5">
 <Label>Fabric SKU</Label>
 <Input
 value={fabricOrderForm.fabric_sku}
 onChange={(e) => setFabricOrderForm({ ...fabricOrderForm, fabric_sku: e.target.value })}
 placeholder="Enter fabric SKU..."
 className="h-10"
 />
 </div>
 </div>
 <div className="space-y-1.5">
 <Label>Notes</Label>
 <Textarea
 value={fabricOrderForm.notes}
 onChange={(e) => setFabricOrderForm({ ...fabricOrderForm, notes: e.target.value })}
 placeholder="Additional notes or details..."
 />
 </div>
 </div>
 <DialogFooter>
 <Button variant="outline" onClick={() => setFabricOrderDialogOpen(false)}>Cancel</Button>
 <Button onClick={handleAddFabricOrder}>Add Fabric Order</Button>
 </DialogFooter>
 </DialogContent>
 </Dialog>

 {/* Test Garment Planner Dialog */}
<Dialog open={testGarmentOpen} onOpenChange={setTestGarmentOpen}>
<DialogContent className="w-[95vw] max-w-[1600px] h-[90vh] p-0 overflow-hidden" data-testid="test-garment-planner-dialog">
<div className="h-full grid grid-cols-12">
  {/* Left Sidebar */}
  <div className="col-span-12 lg:col-span-3 border-r bg-slate-50/80 flex flex-col" data-testid="test-planner-sidebar">
    <div className="p-4 border-b">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-slate-900">Test Planner</h3>
          <p className="text-xs text-slate-600">Plan, configure, review</p>
        </div>
        <Button variant="ghost" size="icon" onClick={() => setTestGarmentOpen(false)} data-testid="test-planner-close-button">
          <X className="w-4 h-4" />
        </Button>
      </div>
      <div className="mt-3 flex items-center gap-2 text-xs">
        <Badge variant={testGarmentStep === 1 ? 'default' : 'outline'}>1 Plan</Badge>
        <Badge variant={testGarmentStep === 2 ? 'default' : 'outline'}>2 Configure</Badge>
        <Badge variant={testGarmentStep === 3 ? 'default' : 'outline'}>3 Review</Badge>
      </div>
    </div>

    {testGarmentStep === 2 && (
      <ScrollArea className="flex-1 p-3">
        <div className="space-y-2">
          {testPlannerQueue.map((item, idx) => {
            const progress = getPlannerItemProgress(item);
            const isActive = idx === testPlannerCurrentIndex;
            return (
              <button
                key={`${item.typeKey}-${idx}`}
                type="button"
                onClick={() => {
                  setTestPlannerCurrentIndex(idx);
                  setTestPlannerSubStep('source');
                }}
                className={`w-full text-left rounded-lg border p-3 transition-all ${isActive ? 'border-primary bg-primary/5' : 'border-slate-200 bg-white hover:border-primary/50'}`}
                data-testid={`test-planner-sidebar-item-${idx}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-medium">{item.testProduct?.name} #{item.slot}</p>
                  <span className={`text-[10px] px-2 py-0.5 rounded border ${progress.color}`}>{progress.label}</span>
                </div>
                <p className="text-xs text-slate-500 mt-1 truncate">{item.sourceGarment?.garment_no || 'Source pending'}</p>
              </button>
            );
          })}
        </div>
      </ScrollArea>
    )}

    {testGarmentStep !== 2 && (
      <div className="p-4 text-xs text-slate-600">
        Configure garments one-by-one in the main panel.
      </div>
    )}
  </div>

  {/* Main Content */}
  <div className="col-span-12 lg:col-span-9 flex flex-col h-full min-h-0">
    {testGarmentStep === 1 && (() => {
      const groups = getQuickMultiMimicGroups();
      const suggested = getSuggestedTestPlannerCounts();
      const counts = {
        jacket: testPlannerCounts.jacket ?? suggested.jacket,
        trouser: testPlannerCounts.trouser ?? suggested.trouser,
        shirt: testPlannerCounts.shirt ?? suggested.shirt,
        waistcoat: testPlannerCounts.waistcoat ?? suggested.waistcoat,
      };
      const labels = { jacket: 'Jacket', trouser: 'Trouser', shirt: 'Shirt', waistcoat: 'Waistcoat' };
      const totalPlanned = Object.values(counts).reduce((sum, val) => sum + (Number(val) || 0), 0);

      return (
        <>
          <div className="px-6 py-5 border-b">
            <h2 className="text-xl font-semibold text-slate-900">Step 1: Plan Test Garments</h2>
            <p className="text-sm text-slate-600 mt-1">Suggested: one test garment per detected type. You can add or remove quantities.</p>
          </div>
          <div className="flex-1 overflow-auto p-6" data-testid="test-planner-step-1">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {Object.keys(labels).map((typeKey) => (
                <div key={typeKey} className="rounded-xl border bg-white p-4 shadow-sm" data-testid={`test-planner-type-card-${typeKey}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-sm">{labels[typeKey]}</p>
                      <p className="text-xs text-slate-600 mt-1">Detected main garments: {groups[typeKey]?.length || 0}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button type="button" variant="outline" size="icon" className="h-8 w-8" onClick={() => handlePlannerCountChange(typeKey, -1)} data-testid={`test-planner-decrease-${typeKey}`}>
                        <Minus className="w-4 h-4" />
                      </Button>
                      <span className="min-w-[28px] text-center font-semibold" data-testid={`test-planner-count-${typeKey}`}>{counts[typeKey] || 0}</span>
                      <Button type="button" variant="outline" size="icon" className="h-8 w-8" onClick={() => handlePlannerCountChange(typeKey, 1)} data-testid={`test-planner-increase-${typeKey}`}>
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-5 rounded-lg border bg-slate-50 px-4 py-3 text-sm" data-testid="test-planner-summary">
              Planned test garments: <span className="font-semibold">{totalPlanned}</span>
            </div>
          </div>
          <div className="px-6 py-4 border-t flex items-center justify-between">
            <Button type="button" variant="outline" onClick={() => setTestPlannerCounts(getSuggestedTestPlannerCounts())} data-testid="test-planner-use-suggested-button">
              Use Suggested
            </Button>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => setTestGarmentOpen(false)}>Cancel</Button>
              <Button type="button" onClick={initializePlannerQueue} disabled={totalPlanned === 0} className="bg-orange-500 hover:bg-amber-600" data-testid="test-planner-step1-proceed-button">
                Proceed to Configure
              </Button>
            </div>
          </div>
        </>
      );
    })()}

    {testGarmentStep === 2 && currentPlannedItem && (() => {
      const groups = getQuickMultiMimicGroups();
      const sourceOptions = groups[currentPlannedItem.typeKey] || [];
      const selectedCategories = currentPlannedItem.categoryIds || [];
      const duplicateSources = testPlannerQueue
        .filter((item, idx) => idx !== testPlannerCurrentIndex)
        .map((item) => item.sourceGarment?.garment_id)
        .filter(Boolean);
      const sourceRepeated = currentPlannedItem.sourceGarment?.garment_id && duplicateSources.includes(currentPlannedItem.sourceGarment.garment_id);

      if (
        testPlannerSubStep === 'source' &&
        !currentPlannedItem.sourceGarment &&
        sourceOptions.length === 1 &&
        !testPlannerSourceAutoPickRef.current[testPlannerCurrentIndex]
      ) {
        handlePlannerSelectSource(sourceOptions[0]);
        testPlannerSourceAutoPickRef.current[testPlannerCurrentIndex] = true;
      }

      const subStepIndex = testPlannerSubStep === 'source' ? 1 : testPlannerSubStep === 'categories' ? 2 : 3;

      return (
        <>
          <div className="px-6 py-5 border-b">
            <h2 className="text-xl font-semibold text-slate-900">Step 2: Configure {currentPlannedItem.testProduct?.name} ({testPlannerCurrentIndex + 1}/{testPlannerQueue.length})</h2>
            <p className="text-sm text-slate-600 mt-1">Sub-step {subStepIndex}/3 • {testPlannerSubStep === 'source' ? 'Select source garment' : testPlannerSubStep === 'categories' ? 'Select categories' : 'Finalize options'}</p>
          </div>

          <div className="px-6 py-3 border-b bg-slate-50 flex items-center gap-2">
            <Badge variant={testPlannerSubStep === 'source' ? 'default' : 'outline'}>1 Source</Badge>
            <Badge variant={testPlannerSubStep === 'categories' ? 'default' : 'outline'}>2 Categories</Badge>
            <Badge variant={testPlannerSubStep === 'options' ? 'default' : 'outline'}>3 Options</Badge>
            {sourceOptions.length === 1 && (
              <span className="text-xs text-green-700 bg-green-50 border border-green-200 rounded px-2 py-0.5 ml-2" data-testid="test-planner-single-source-autopick-note">
                Auto-picked source (single option)
              </span>
            )}
          </div>

          <div className="flex-1 min-h-0 overflow-hidden p-6" data-testid="test-planner-step-2">
            {testPlannerSubStep === 'source' && (
              <div className="space-y-3">
                <Label className="text-sm font-medium">Source Garment</Label>
                <Select
                  value={currentPlannedItem.sourceGarment?.line_item_id || '__none__'}
                  onValueChange={(value) => {
                    const selected = sourceOptions.find((opt) => opt.line_item_id === value);
                    if (selected) handlePlannerSelectSource(selected);
                  }}
                >
                  <SelectTrigger data-testid="test-planner-source-select-trigger">
                    <SelectValue placeholder="Select source garment" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Select source garment</SelectItem>
                    {sourceOptions.map((item) => (
                      <SelectItem key={item.line_item_id} value={item.line_item_id} data-testid={`test-planner-source-option-${item.line_item_id}`}>
                        {item.source_garment_no || item.line_item_ref} - {item.product_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {sourceRepeated && (
                  <p className="text-xs text-amber-700" data-testid="test-planner-duplicate-source-warning">
                    Warning: same source garment selected in another planned test item.
                  </p>
                )}
              </div>
            )}

            {testPlannerSubStep === 'categories' && (
              <div className="space-y-3">
                <Label className="text-sm font-medium">Categories to Copy</Label>
                <div className="grid gap-2 max-h-[48vh] overflow-y-auto">
                  {(currentPlannedItem.productCustomizations?.categories || []).map((category) => {
                    const isSelected = selectedCategories.includes(category.category_id);
                    const isFabricCategory = category.name?.toLowerCase().includes('fabric');
                    return (
                      <button
                        key={category.category_id}
                        type="button"
                        onClick={() => handlePlannerToggleCategory(category.category_id)}
                        className={`p-3 rounded-lg border transition-all text-left flex items-center gap-3 ${isSelected ? 'border-primary bg-primary/10' : 'border-slate-200 hover:border-primary/50'}`}
                        data-testid={`test-planner-category-toggle-${category.category_id}`}
                      >
                        <Checkbox checked={isSelected} />
                        <div className="flex-1">
                          <p className="font-medium text-sm">{category.name}</p>
                          <p className="text-xs text-muted-foreground">{category.options?.length || 0} options {isFabricCategory ? '• Will use TEST FABRIC' : ''}</p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {testPlannerSubStep === 'options' && (
              <div className="space-y-3 h-full min-h-0 flex flex-col" data-testid="test-planner-options-substep-container">
                <Label className="text-sm font-medium">Finalize Options</Label>
                <ScrollArea className="h-[52vh] rounded-md border bg-white p-3" data-testid="test-planner-options-scroll-container">
                  <div className="space-y-3 pr-2">
                    {(currentPlannedItem.productCustomizations?.categories || [])
                      .filter((category) => selectedCategories.includes(category.category_id))
                      .map((category) => (
                        <div key={category.category_id} className="rounded-lg border bg-slate-50 p-3">
                          <p className="text-sm font-medium mb-2">{category.name}</p>
                          <div className="grid md:grid-cols-2 gap-2">
                            {(category.options || []).map((option) => {
                              const value = currentPlannedItem.customValues?.[option.option_id] || '';
                              const isTestFabric = isMainOrLiningFabricOption(option);
                              return (
                                <div key={option.option_id} className="space-y-1">
                                  <Label className="text-xs text-muted-foreground">{option.name}</Label>
                                  <Input
                                    value={isTestFabric ? 'TEST FABRIC' : value}
                                    onChange={(e) => handlePlannerCustomValueChange(option.option_id, e.target.value)}
                                    placeholder={option.placeholder || `Enter ${option.name.toLowerCase()}`}
                                    className="h-9 text-sm"
                                    disabled={isTestFabric}
                                    data-testid={`test-planner-option-input-${option.option_id}`}
                                  />
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                  </div>
                </ScrollArea>
                {selectedCategories.length === 0 && (
                  <p className="text-xs text-slate-600">No categories selected for this garment.</p>
                )}
              </div>
            )}
          </div>

          <div className="px-6 py-4 border-t flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => setTestGarmentStep(1)}>Back to Plan</Button>
              <Button variant="outline" onClick={handlePlannerPrevItem} disabled={testPlannerCurrentIndex === 0 && testPlannerSubStep === 'source'}>
                Previous
              </Button>
            </div>
            <Button onClick={handlePlannerNextItem} className="bg-orange-500 hover:bg-amber-600" data-testid="test-planner-next-item-button">
              {testPlannerSubStep !== 'options' ? 'Next' : (testPlannerCurrentIndex === testPlannerQueue.length - 1 ? 'Review All' : 'Save & Next Garment')}
            </Button>
          </div>
        </>
      );
    })()}

    {testGarmentStep === 3 && (
      <>
        <div className="px-6 py-5 border-b">
          <h2 className="text-xl font-semibold text-slate-900">Step 3: Review Planned Test Garments</h2>
          <p className="text-sm text-slate-600 mt-1">Expand each garment to inspect selected categories and option values.</p>
        </div>

        <div className="flex-1 overflow-auto p-6" data-testid="test-planner-step-3">
          <Accordion type="multiple" className="w-full space-y-2" data-testid="test-planner-review-accordion">
            {testPlannerQueue.map((item, idx) => {
              const progress = getPlannerItemProgress(item);
              const selectedCategorySet = new Set(item.categoryIds || []);
              const selectedCategories = (item.productCustomizations?.categories || []).filter((c) => selectedCategorySet.has(c.category_id));
              return (
                <AccordionItem key={`${item.typeKey}-${idx}`} value={`item-${idx}`} className="rounded-lg border bg-white px-4">
                  <AccordionTrigger className="hover:no-underline" data-testid={`test-planner-review-accordion-trigger-${idx}`}>
                    <div className="flex items-center justify-between w-full pr-2">
                      <div>
                        <p className="text-sm font-semibold">{item.testProduct?.name} #{item.slot}</p>
                        <p className="text-xs text-slate-500">Source: {item.sourceGarment?.garment_no || 'Not selected'} • Categories: {(item.categoryIds || []).length}</p>
                      </div>
                      <span className={`text-[10px] px-2 py-0.5 rounded border ${progress.color}`}>{progress.label}</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-3 pb-2" data-testid={`test-planner-review-accordion-content-${idx}`}>
                      {selectedCategories.map((category) => (
                        <div key={category.category_id} className="rounded-md border bg-slate-50 p-3">
                          <p className="text-xs font-semibold text-slate-700 mb-2">{category.name}</p>
                          <div className="grid md:grid-cols-2 gap-2">
                            {(category.options || []).map((option) => {
                              const value = isMainOrLiningFabricOption(option)
                                ? 'TEST FABRIC'
                                : (item.customValues?.[option.option_id] || '');
                              return (
                                <div key={option.option_id} className="text-xs">
                                  <span className="text-slate-500">{option.name}: </span>
                                  <span className="font-medium text-slate-800">{value || '—'}</span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                      {selectedCategories.length === 0 && (
                        <p className="text-xs text-slate-600">No categories selected.</p>
                      )}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        </div>

        <div className="px-6 py-4 border-t flex items-center justify-between">
          <Button variant="outline" onClick={() => { setTestGarmentStep(2); setTestPlannerSubStep('source'); }}>
            Back to Configure
          </Button>
          <Button
            onClick={handleCreateAllPlannedTestGarments}
            disabled={
              testPlannerSubmitting ||
              testPlannerQueue.some((item) => !item.sourceGarment) ||
              testPlannerQueue.some((item) => (item.productCustomizations?.categories?.length || 0) > 0 && (item.categoryIds || []).length === 0)
            }
            className="bg-orange-500 hover:bg-amber-600"
            data-testid="test-planner-create-all-button"
          >
            {testPlannerSubmitting ? 'Creating...' : `Create ${testPlannerQueue.length} Test Garment(s)`}
          </Button>
        </div>
      </>
    )}
  </div>
</div>
</DialogContent>
</Dialog>


{/* Delete Garment Confirmation Dialog */}
 <AlertDialog open={deleteGarmentDialogOpen} onOpenChange={setDeleteGarmentDialogOpen}>
 <AlertDialogContent>
 <AlertDialogHeader>
 <AlertDialogTitle>Delete Garment</AlertDialogTitle>
 <AlertDialogDescription>
 Are you sure you want to delete this garment?
 {garmentToDelete?.line_item_id && (
 <div className="mt-3 p-3 bg-muted rounded-lg">
 <p className="text-sm font-medium mb-2">This garment is linked to a line item. What would you like to do?</p>
 <div className="space-y-2">
 <label className="flex items-center space-x-2">
 <input
 type="radio"
 name="deleteOption"
 value="garment_only"
 checked={deleteGarmentOption === 'garment_only'}
 onChange={(e) => setDeleteGarmentOption(e.target.value)}
 className="text-primary"
 />
 <span className="text-sm">Delete garment only (preserve line item)</span>
 </label>
 <label className="flex items-center space-x-2">
 <input
 type="radio"
 name="deleteOption"
 value="with_line_item"
 checked={deleteGarmentOption === 'with_line_item'}
 onChange={(e) => setDeleteGarmentOption(e.target.value)}
 className="text-primary"
 />
 <span className="text-sm">Delete garment and line item</span>
 </label>
 </div>
 </div>
 )}
 </AlertDialogDescription>
 </AlertDialogHeader>
 <AlertDialogFooter>
 <AlertDialogCancel onClick={() => {
 setDeleteGarmentDialogOpen(false);
 setGarmentToDelete(null);
 }}>
 Cancel
 </AlertDialogCancel>
 <AlertDialogAction 
 onClick={handleConfirmDeleteGarment}
 className="bg-red-600 hover:bg-red-700"
 >
 Delete
 </AlertDialogAction>
 </AlertDialogFooter>
 </AlertDialogContent>
 </AlertDialog>

 {/* Garment Images Modal */}
 <GarmentImagesModal
 isOpen={garmentImagesOpen}
 onClose={() => {
 setGarmentImagesOpen(false);
 setSelectedGarment(null);
 }}
 garment={selectedGarment}
 onUpdate={fetchOrderData}
 />

 {/* Delete Field Confirmation Dialog */}
 <AlertDialog open={deleteFieldDialogOpen} onOpenChange={setDeleteFieldDialogOpen}>
 <AlertDialogContent>
 <AlertDialogHeader>
 <AlertDialogTitle>Remove Field from Line Item?</AlertDialogTitle>
 <AlertDialogDescription>
 <div className="space-y-3">
 <p>
 You are about to remove <strong>{fieldToDelete?.option?.name}</strong> from this line item.
 </p>
 {fieldToDelete?.option?.is_required && (
 <div className="p-3 bg-orange-50 border border-orange-200 rounded-md">
 <p className="text-sm text-orange-800 flex items-center gap-2">
 <AlertCircle className="w-4 h-4" />
 <span><strong>Warning:</strong> This is a required field. Removing it may affect the completeness of this order.</span>
 </p>
 </div>
 )}
 <p className="text-sm text-muted-foreground">
 This change only affects this specific line item. The field will remain in the product&apos;s master configuration.
 </p>
 </div>
 </AlertDialogDescription>
 </AlertDialogHeader>
 <AlertDialogFooter>
 <AlertDialogCancel onClick={() => {
 setDeleteFieldDialogOpen(false);
 setFieldToDelete(null);
 }}>
 Cancel
 </AlertDialogCancel>
 <AlertDialogAction 
 onClick={confirmDeleteField}
 className="bg-red-600 hover:bg-red-700"
 >
 Remove Field
 </AlertDialogAction>
 </AlertDialogFooter>
 </AlertDialogContent>
 </AlertDialog>

 {/* Add Custom Field Dialog */}
 <Dialog open={addCustomFieldDialogOpen} onOpenChange={setAddCustomFieldDialogOpen}>
 <DialogContent className="sm:max-w-md">
 <DialogHeader>
 <DialogTitle>Add Custom Field</DialogTitle>
 <DialogDescription>
 Add a custom text field to this line item. This field will only appear on this specific line item.
 </DialogDescription>
 </DialogHeader>
 <div className="space-y-4 py-4">
 <div className="space-y-2">
 <Label htmlFor="field_name">Field Name *</Label>
 <Input
 id="field_name"
 value={customFieldForm.field_name}
 onChange={(e) => setCustomFieldForm({
 ...customFieldForm,
 field_name: e.target.value
 })}
 placeholder="e.g., Special Instructions"
 className="h-10"
 />
 </div>
 <div className="space-y-2">
 <Label htmlFor="field_value">Field Value</Label>
 <Textarea
 id="field_value"
 value={customFieldForm.field_value}
 onChange={(e) => setCustomFieldForm({
 ...customFieldForm,
 field_value: e.target.value
 })}
 placeholder="Enter the value for this field..."
 className="min-h-[80px]"
 rows={3}
 />
 <p className="text-xs text-muted-foreground">You can leave this empty and fill it later</p>
 </div>
 </div>
 <DialogFooter>
 <Button
 variant="outline"
 onClick={() => {
 setAddCustomFieldDialogOpen(false);
 setCustomFieldForm({ field_name: '', field_value: '' });
 }}
 >
 Cancel
 </Button>
 <Button onClick={handleConfirmAddCustomField}>
 Add Field
 </Button>
 </DialogFooter>
 </DialogContent>
 </Dialog>

 {/* Add Garment Dialog */}
 <AddGarmentDialog
 open={addGarmentDialogOpen}
 onOpenChange={setAddGarmentDialogOpen}
 orderId={orderId}
 orderNumber={orderData?.order_number}
lineItemsWithRefs={sortedLineItemsWithRefs}
existingGarments={sortedGarments}
 factories={factories}
 onGarmentCreated={handleGarmentCreated}
 apiUrl={getApiBaseUrl()}
 token={localStorage.getItem('token')}
 initialGarmentToRemake={remakeGarmentData}
 />

</>
)}

 {/* Floating Save Status Indicator */}
 {(saveStatus === 'saving' || saveStatus === 'saved' || saveStatus === 'error') && (
 <div className="fixed top-4 right-4 z-50 animate-in fade-in slide-in-from-top-2 duration-200">
 <div className={`
 px-4 py-2 rounded-lg shadow-lg border flex items-center gap-2
 ${saveStatus === 'saving' ? 'bg-blue-50 border-blue-200 text-blue-700' : ''}
 ${saveStatus === 'saved' ? 'bg-green-50 border-green-200 text-green-700' : ''}
 ${saveStatus === 'error' ? 'bg-red-50 border-red-200 text-red-700' : ''}
 `}>
 {saveStatus === 'saving' && (
 <>
 <Loader2 className="w-4 h-4 animate-spin" />
 <span className="text-sm font-medium">Saving...</span>
 </>
 )}
 {saveStatus === 'saved' && (
 <>
 <CheckCircle2 className="w-4 h-4" />
 <span className="text-sm font-medium">All changes saved</span>
 </>
 )}
 {saveStatus === 'error' && (
 <>
 <AlertCircle className="w-4 h-4" />
 <span className="text-sm font-medium">Failed to save</span>
 </>
 )}
 </div>
 </div>
 )}
</div>
 </div>
 );
};

export default EditOrderPage;
